import React, {useState, useEffect, useRef} from 'react'
import {ParagraphText} from '@thryvlabs/maverick'
import {client} from '../utils/api-client'
import {toast} from 'react-toastify'
import {AppSpinner, SummarySpinner} from '../components/custom/loader-spinner'
import calculatingTaxesGif from '../assets/calculating-tax-image.png'
import thryvPeek from '../assets/thryv-peek-2.png'
import {
  KCPPaymentDetails,
  computeTabTotal,
  getCoreTabItemCount,
  getLocationPreviewSummary,
} from '../components/subscription-summary'
import {PaymentMethodComponent} from '../components/payment-method'
import {useLocation, useNavigate} from 'react-router-dom'
import {AgreedTermNote} from '../components/agreed-term-note'
import {formatInCodeQuantityStructure, formatSubDetails} from '../utils/util'
import {
  applyOwnedSubscriptionPrices,
  attachLocationCoupons,
  attachLocationPreviews,
  buildCorePaymentCart,
  fetchLocationNames,
  fetchOwnedSubscriptionsByLocation,
  getCorePaymentPlanCodes,
  getPurchasePlanPath,
} from '../utils/core-payment-cart'
import {useAsync} from '../utils/use-async'
import {LoadingTimelineModal} from '../components/custom/loading-timeline'
import dayjs from 'dayjs'
import {ThryvAndKeapHeader} from './order-details'
import {GradientTabs, GradientTab} from '../core/shared/GradientTabs'
import {formatPrice, ccCurrencyCodes} from '../utils/currency-format'
import logo from '../assets/thryv-logo-white-letters.svg'
import keapLogo from '../assets/keap_brand_logo.svg'
import ExpiryCountdownTimer from '../components/expiry-countdown-timer'
import {
  isInternationalTaxIdCountry,
  isUsdOnlyCountry,
  normalizeTaxIdentifier,
} from '../utils/country-registry'
import '../styles/formScreensStyles.css'
import {kP11LinkService} from '../components/keap-scheduler-v2/kp-11Link-service'
import {useBillingInfo} from '../components/keap-partner/payment-information-components/contexts/billing-info-context'
import {
  PAYMENT_METHODS,
  getPaymentMethods,
  getPaymentDisclaimer,
} from '../components/keap-partner/payment-information-components/constants'
import {Toggle} from '../core/shared/Toggle'
import {AchTransferIcon, CardPaymentIcon, ArrowRightIcon} from '../assets'
import {usePaymentOrder} from '../components/keap-partner/payment-information-components/hooks/use-payment-order'
import {useRecurlyPayment} from '../components/keap-partner/payment-information-components/contexts/recurly-payment-context'
import {usePaymentCheckboxes} from '../components/keap-partner/payment-information-components/contexts/payment-checkboxes-context'
import {OverlayLoader} from '../ui-view/payment-information'
import {useDispatch, useSelector} from 'react-redux'
import {clearRecurlyTokenData} from '../redux/recurlySlice'
import {AccountFailedModalComponent} from '../components/account-failed-modal'
import {usesInfluencerScheduling} from '../utils/partner-scheduling'

const dataJSON = {}
const ONE_HOUR_MS = 3600000
const NON_ADDED_PLANS = [
  'addon-5-signatures_usage',
  'app-websitebuilder',
  'cc_basicpls-5-thryv-m2m',
  'thryvprfee',
]
const RETRY_COUNT_LIMIT = 5
const DELAY_IN_MS = 5000

const CORE_ADDRESS_FIELDS = ['street', 'city', 'state', 'postalCode']

const hasCompleteCoreBusinessAddress = address =>
  CORE_ADDRESS_FIELDS.every(field => String(address?.[field] || '').trim())

// A Core payment link stores its checkout request in plan_code so OOECP can
// create the payment order without reinterpreting the cart. Older/new-account
// links can have an incomplete address in that snapshot, even though the
// location account has already been provisioned with one. Resolve only the
// missing fields from that authoritative child account; never overwrite the
// address deliberately supplied on the link.
const hydrateCorePaymentAddress = async (client, corePaymentPayload) => {
  const originalAddress = corePaymentPayload?.businessAddress || {}
  if (hasCompleteCoreBusinessAddress(originalAddress)) {
    return corePaymentPayload
  }

  const locationThryvId =
    corePaymentPayload?.locationDetails?.[0]?.locationThryvId ||
    corePaymentPayload?.thryvId

  if (!locationThryvId) {
    throw new Error('This payment link is missing its location account.')
  }

  const account = await client(
    `orders/validate-emailaccount/${encodeURIComponent(locationThryvId)}`,
  )
  const billingAddress = account?.billingAddress || {}
  const resolvedAddress = {
    street:
      originalAddress.street ||
      account?.address ||
      billingAddress.street1 ||
      '',
    city: originalAddress.city || account?.city || billingAddress.city || '',
    state:
      originalAddress.state || account?.state || billingAddress.region || '',
    postalCode:
      originalAddress.postalCode ||
      account?.postCode ||
      billingAddress.postalCode ||
      '',
    country: originalAddress.country || billingAddress.country || 'US',
    phone:
      originalAddress.phone || account?.phone || billingAddress.phone || '',
  }

  if (!hasCompleteCoreBusinessAddress(resolvedAddress)) {
    throw new Error(
      'A complete business address is required before this payment link can be used.',
    )
  }

  return {...corePaymentPayload, businessAddress: resolvedAddress}
}

// All known Keap Add-Ons parent plan codes in Recurly.
// OOE checks the client's existing subscriptions and routes add-ons under whichever
// parent plan is already active, so both legacy and current clients are supported.
//   Current monthly : addon-5-keap-addons
//   Current annual  : addon-5-keap-addons-annual
//   Legacy  monthly : addon-5-keap-addons-legacy-m2m
//   Legacy  annual  : addon-5-keap-addons-legacy-annual
const KEAP_ADDONS_PARENT_PLAN_CODES = [
  'addon-5-keap-addons', // current monthly
  'addon-5-keap-addons-annual', // current annual
  'addon-5-keap-addons-legacy-m2m', // legacy monthly
  'addon-5-keap-addons-legacy-annual', // legacy annual
]

function MobilePaymentHeader({
  businessName,
  linkExpiryInMilliSec,
  totalAmount,
  hasTax,
  onBack,
}) {
  return (
    <header
      style={{
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        width: '100%',
        paddingTop: '26px',
        paddingInline: '12px',
      }}
    >
      {/* Row 1: logos */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
          alignItems: 'center',
          marginBlock: '8px',
        }}
      >
        <div style={{display: 'flex', alignItems: 'center'}}>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'white',
                fontFamily: 'Open Sans',
                fontSize: '13px',
                fontWeight: 600,
                padding: 0,
                flexShrink: 0,
              }}
            >
              <svg
                width="17"
                height="9"
                viewBox="0 0 17 9"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3.63509 8.02841C3.63396 8.02729 3.63285 8.02616 3.63173 8.02502L0.160293 4.529C0.132896 4.50123 0.108562 4.47053 0.0876954 4.43748L0.0332415 4.33681L0.0105453 4.26817L0.0105453 4.21783C-0.00351486 4.1407 -0.00351486 4.06161 0.0105453 3.98446L0.0105453 3.93871L0.0105453 3.88379L0.0513857 3.80599C0.0747413 3.76265 0.103734 3.72262 0.137597 3.68701L3.63173 0.177268C3.86294 -0.057757 4.23933 -0.0592799 4.4724 0.173879C4.47353 0.174995 4.47463 0.176132 4.47576 0.177268C4.69992 0.414181 4.69992 0.786893 4.47576 1.02383L2.40652 3.11503C2.31879 3.20525 2.3202 3.35012 2.40968 3.43858C2.45131 3.47974 2.50704 3.50317 2.56535 3.50398L16.4055 3.50398C16.7338 3.50396 17 3.77232 17 4.1034C17 4.43447 16.7339 4.70285 16.4056 4.7029L2.56535 4.7029C2.44006 4.70468 2.33989 4.80854 2.34166 4.93488C2.34249 4.99365 2.3657 5.04987 2.40652 5.09185L4.47576 7.17391C4.70281 7.41143 4.70281 7.78753 4.47576 8.02504C4.24455 8.26007 3.86816 8.26159 3.63509 8.02841Z"
                  fill="white"
                />
              </svg>
              Back
            </button>
          )}
          <img
            src={logo}
            style={{marginLeft: 'auto'}}
            alt="Thryv Logo"
            className="header-logo"
          />
        </div>
        <div
          style={{
            height: '36px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            marginInline: '10px',
            flexShrink: 0,
          }}
        />
        <div style={{display: 'flex', alignItems: 'center'}}>
          <img
            src={keapLogo}
            alt="Keap Logo"
            className="header-logo"
            style={{filter: 'brightness(0) invert(1)'}}
          />
        </div>
      </div>

      {/* Row 2: business name | timer */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
          alignItems: 'flex-start',
          paddingBottom: '10px',
        }}
      >
        <div style={{display: 'flex', flexDirection: 'column'}}>
          {businessName && (
            <>
              <span className="payment-header-label">
                You&apos;re paying for
              </span>
              <span className="payment-header-value" style={{fontSize: '12px'}}>
                {businessName}
              </span>
            </>
          )}
        </div>
        <div
          style={{
            height: '57px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            marginInline: '10px',
            flexShrink: 0,
          }}
        />
        <div style={{display: 'flex', justifyContent: 'flex-end'}}>
          {linkExpiryInMilliSec > 0 && (
            <ExpiryCountdownTimer
              countdownTimeMS={linkExpiryInMilliSec}
              isHourlyNeeded={linkExpiryInMilliSec > 60 * 60 * 1000}
              isMobileView={false}
              unitBelow={true}
            />
          )}
        </div>
      </div>

      {/* Horizontal separator */}
      <div
        style={{
          border: '1px solid rgba(255, 255, 255, 0.1)',
          marginInline: '-10px',
        }}
      />

      {/* Row 3: Order Summary | Total — hidden on payment step */}
      <div
        style={{
          display: onBack ? 'none' : 'grid',
          gridTemplateColumns: '1fr auto',
          alignItems: 'center',
          paddingBlock: '10px',
          gap: '10px',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            paddingRight: '35px',
          }}
        >
          <span
            className="payment-header-value"
            style={{fontSize: '18px', lineHeight: '100%', letterSpacing: '0%'}}
          >
            Order Summary
          </span>
          <span className="payment-header-note" style={{marginTop: '10px'}}>
            Future invoices will include any eligible discount for as long as
            your account qualifies.
          </span>
        </div>
        <div className="payment-total-container">
          {totalAmount && (
            <>
              <p className="payment-total-label">Total</p>
              <p className="payment-total-amount">
                {totalAmount}
                {hasTax && <span className="payment-total-label"> + Tax</span>}
              </p>
            </>
          )}
        </div>
      </div>
    </header>
  )
}

export const PaymentDetailsComponent = () => {
  const {isFetching, isVerifying, setRecurlyAccountId, setIsFetching} =
    useBillingInfo()
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS[0])
  const {checkBoxesValues, setCheckBoxesValues, isAgreeCheckboxDisabled} =
    usePaymentCheckboxes()
  //const [agreedTermValue, setAgreedTermValue] = useState(false)
  const [cartDetailObj, setCartDetailObj] = useState({})
  // True until the order summary's data (catalog, owned prices, coupons) is ready.
  const [isSummaryLoading, setIsSummaryLoading] = useState(true)
  const [subscriptionList, setSubscriptionList] = useState({})
  const [preselectedSubData, setPreselectedSubData] = useState({})
  const [country, setCountry] = useState('')
  // {[locationThryvId]: company name} for a multi-location (parent-pay) link —
  // the link payload only carries each location's thryvId.
  const [locationNames, setLocationNames] = useState({})
  const [payloadData, setPayloadData] = useState({})
  const [paymentUUID, setPaymentUUID] = useState('')
  const [orderId, setOrdreId] = useState('')
  const setRecurlyToken = () => {} // Placeholder
  const [isTaxCalculated, setTaxCalculateStatus] = useState(false)
  // {[locationThryvId]: preview} of a multi-location link, once billing is saved.
  const [locationPreviews, setLocationPreviews] = useState(null)
  const thryvId = useRef('')
  const [isLoading, setIsLoading] = useState(false)
  const [calculatingTaxes, setCalculatingTaxes] = useState(false)
  const [billingTax, setBillingTax] = useState(null)
  const finalBillingAmountDetails = billingTax != null ? {tax: billingTax} : {}
  const [previewDetails, setPreviewDetails] = useState({})
  const [scheduleDetails, setScheduleDetails] = useState([])
  const [isKCCExist, setKCCExistFlag] = useState(false)
  const [activeRightTab, setActiveRightTab] = useState('today')
  const [mobileStep, setMobileStep] = useState(1)
  const [hasEnteredStep2, setHasEnteredStep2] = useState(false)
  const [linkExpiryInMinutes, setLinkExpiryInMinutes] = useState(0)
  const currentErrorStep = useRef('accountCreationStep')
  // Guards preLoadApiCall() against firing twice on the same mount (e.g. a double-effect
  // invocation) — see createThryvAccount()'s sessionStorage guard for the cross-remount case.
  const preLoadStartedRef = useRef(false)
  const [listItem, setListItem] = useState({
    centers: [],
    apps: [],
    oneTimeFees: [],
  })
  const navData = useLocation()
  const currentStep = useRef(1)
  const showErrorAccountModal = useRef(false)
  const {paymentCurrentStep} = useSelector(state => state.paymentScreenView)

  const dispatch = useDispatch()
  console.log({paymentCurrentStep})
  const captureOrderHeaderId = useRef(null)
  const [isLoadingTimelineCompleted, setLoadingTimelineCompletionStatus] =
    useState(false)
  // True only once preLoadApiCall reaches the branch that really creates an account
  // (1:many link), so the "Creating Account" modal never shows for 1:1 or core links.
  const [isAccountCreationFlow, setIsAccountCreationFlow] = useState(false)
  const [hasScrolledToTop, setHasScrolledToTop] = useState(false)

  const {run} = useAsync({
    status: 'pending',
  })
  const navigate = useNavigate()
  const transitionData = useLocation()
  // What the backend says is due: the whole-order preview, or for a multi-location
  // link the sum of its per-location previews.
  const displayPreview =
    previewDetails?.paymentPreview ??
    getLocationPreviewSummary(transitionData.state?.orderDetail)
  const linkType = navData?.state?.linkType
  // The fetched link is authoritative: 1:many links have no order_id, while
  // influencer links carry influencer_name. Do not base this exception on
  // navigation state, which is not a trusted source and is lost on refresh.
  const isInfluencerOneToMany =
    Boolean(transitionData?.state?.keapPartnerLinkDetail?.influencer_name) &&
    !transitionData?.state?.keapPartnerLinkDetail?.order_id
  const allowCountryChange =
    isInfluencerOneToMany && ['US', 'CA'].includes(country)
  const _isLoading = isLoading || isFetching || isVerifying
  // Amounts are hidden while either spinner (busy flags or the account-creation
  // timeline modal) is up, so a not-yet-final estimate never flashes. Wider than
  // isSummaryLoading, which clears while the timeline modal is still showing.
  const isSummaryBusy =
    isSummaryLoading || _isLoading || !isLoadingTimelineCompleted
  // Full-page overlay while the summary loads so the card/billing area cannot be
  // edited early. Skipped when the creation modal or the existing isLoading
  // spinner is already up, to avoid stacking a second spinner.
  const showPageSpinner =
    isSummaryBusy &&
    !isLoading &&
    !(isAccountCreationFlow && !isLoadingTimelineCompleted)
  const {isCVCVerified} = useBillingInfo()
  const {is5Order} = usePaymentOrder()
  const {clearRecurlyToken, recurlyToken, setType} = useRecurlyPayment()
  //const {isAgreeCheckboxDisabled} = usePaymentCheckboxes()
  // const agreedTerms = checkBoxesValues.agreedTerms
  const {isMobileViewEnable} = useSelector(state => state.screenView)
  const {recurlyTokenDetail} = useSelector(state => state.recurlyTokenData)

  useEffect(() => {
    setPaymentMethod(PAYMENT_METHODS[0])
  }, [isMobileViewEnable])

  useEffect(() => {
    let countryCode
    let currency

    const getOrderDetail = async () => {
      try {
        const id = navData?.state?.id || navData?.state?.linkId

        let data = await client(`cc/order/keapClient/fetchOrder?id=${id}`)
        if (data?.plan_code?.plan) {
          setKCCExistFlag(
            data?.plan_code?.plan.some(
              item =>
                (item.code.includes('keap_kcp-cert-5') ||
                  item.code.includes('keap_kcc-cert-5')) &&
                !item.isAlreadyAdded,
            ),
          )
        }
        countryCode = navData?.state?.country || data.country_code
        setCountry(countryCode)
        currency = ccCurrencyCodes[countryCode] || 'USD'

        let subList = await client(`plans/5/v1/list`)
        setSubscriptionList(subList)
        setPreselectedSubData(data)
        console.log(preselectedSubData)
        transitionData.state.keapPartnerLinkDetail = data
        transitionData.state.linkId = data.id
        transitionData.state.slpShareLinkId = data.slp_share_link_id
        // Core multi-location links create their cc order from the persisted
        // Core payload in preLoadApiCall below. Do not overwrite that newly
        // returned UUID/order id with the legacy link record's empty fields.
        if (!data?.plan_code?.corePaymentPayload) {
          setPaymentUUID(data.payment_uuid)
          setOrdreId(data.cc_order_id)
        }
        if (data.created_date) {
          try {
            const expiryConfig = await run(
              client(
                'keappartner/fetchConfigLookupValue/KeapPartnerLinkExpiryInMinutes',
              ),
            )
            const expiryMinutes = expiryConfig?.data?.length
              ? Number(expiryConfig.data[0].value)
              : 14 * 24 * 60
            // Show 1 hour less than the actual backend expiry per product spec
            const expiryMs =
              new Date(data.created_date).getTime() +
              expiryMinutes * 60000 -
              Date.now() -
              ONE_HOUR_MS
            setLinkExpiryInMinutes(Math.max(0, expiryMs))
          } catch {
            // fallback: 14 days minus 1 hour
            const expiryMs =
              new Date(data.created_date).getTime() +
              14 * 24 * 60 * 60 * 1000 -
              Date.now() -
              ONE_HOUR_MS
            setLinkExpiryInMinutes(Math.max(0, expiryMs))
          }
        }
        console.log('keapPartnerLinkDetail data:', data)
        console.log(
          'country_code from keapPartnerLinkDetail:',
          data?.country_code,
        )

        const thryvID = navData?.state?.custThryvId
        let recurlySubscriptions
        // Only the legacy cart below uses the account's existing subscriptions;
        // Core links skip the (slow) Recurly lookup so the summary renders sooner.
        if (thryvID && !data?.plan_code?.corePaymentPayload) {
          try {
            recurlySubscriptions = await client(
              `accounts/5/subscriptions?thryvId=${thryvID}`,
            )
            console.log('recurlySubscriptions:', recurlySubscriptions)
          } catch (err) {
            console.log('Error fetching recurly subscriptions:', err)
          }
        }

        let pendingPreviewResponse = null
        if (
          linkType === '1:1' &&
          data?.payment_uuid &&
          !data?.plan_code?.corePaymentPayload
        ) {
          console.log('inside the getOrder Details ===>', {data})
          try {
            let previewResponse = await client(
              `cc/order/purchase-plan/${data.payment_uuid}`,
            )
            // console.log({previewResponse})
            pendingPreviewResponse = previewResponse
            if (previewResponse?.recurlyAccountId) {
              setRecurlyAccountId(previewResponse?.recurlyAccountId)

              // Detect which Keap Add-Ons parent plan the client already has in Recurly
              // (could be current monthly/annual or legacy monthly/annual) and override
              // the wrapper code in the order data so add-on subscriptions are created
              // under the correct existing parent plan.
              const hasKeapAddons = (data.add_on_code?.addon || []).some(
                addon =>
                  String(addon.code || '').startsWith('addon-5-keap-addons'),
              )
              if (hasKeapAddons) {
                try {
                  const existingSubscriptions = await client(
                    `accounts/${previewResponse.recurlyAccountId}/subscriptions`,
                  )
                  const existingKeapAddonsSub = (
                    existingSubscriptions || []
                  ).find(sub =>
                    KEAP_ADDONS_PARENT_PLAN_CODES.includes(sub?.plan?.code),
                  )
                  if (existingKeapAddonsSub?.plan?.code) {
                    data.add_on_code.addon = data.add_on_code.addon.map(
                      addon =>
                        String(addon.code || '').startsWith(
                          'addon-5-keap-addons',
                        )
                          ? {...addon, code: existingKeapAddonsSub.plan.code}
                          : addon,
                    )
                  }
                } catch (err) {
                  console.log('Error detecting Keap Add-Ons parent plan:', err)
                }
              }
            }
          } catch (err) {
            console.log(err)
            return undefined
          }
          try {
            const scheduleData = await client(
              `keappartner/fetchScheduleDetails/${data.order_id}`,
            )
            if (scheduleData.success) {
              setScheduleDetails(scheduleData.data)
            }
          } catch (err) {
            console.log(err)
          }
        }
        const catalogList = [
          ...(subList?.centers || []),
          ...(subList?.apps || []),
          ...(subList?.oneTimeFees || []),
        ]
        const corePaymentPayload = data?.plan_code?.corePaymentPayload
        if (corePaymentPayload) {
          // Owned lines show what the account really pays (same as the core OOE
          // payment link); a failed lookup just keeps their catalog price.
          const cartObj = buildCorePaymentCart(
            applyOwnedSubscriptionPrices(
              corePaymentPayload,
              await fetchOwnedSubscriptionsByLocation(
                client,
                corePaymentPayload,
              ),
            ),
            subList,
            currency,
          )
          setCartDetailObj(cartObj)
          transitionData.state.orderDetail = cartObj
          // Validate the link's coupons up front (like the legacy flow below) so
          // Coupons & Discounts shows before billing is saved, not only once the
          // backend purchase preview succeeds.
          const planCodes = getCorePaymentPlanCodes(cartObj)
          // Validated in parallel (order preserved) — each is its own round trip.
          const coreCoupons = (
            await Promise.all(
              [...new Set(corePaymentPayload.coupons || [])].map(async code => {
                try {
                  return await client(
                    `orders/coupon/validate?couponCode=${code}&country=${countryCode}&planCode=${planCodes.join()}`,
                  )
                } catch (err) {
                  console.log('Error validating coupon', code, err)
                  return null
                }
              }),
            )
          ).filter(couponDetails => couponDetails?.isValid)
          transitionData.state.couponList = coreCoupons
          // A multi-location link can carry each location's own coupons; show
          // them inside that location (links without them keep the order-level
          // list).
          const cartWithLocationCoupons = attachLocationCoupons(
            cartObj,
            corePaymentPayload,
            coreCoupons,
          )
          if (cartWithLocationCoupons !== cartObj) {
            setCartDetailObj(cartWithLocationCoupons)
            transitionData.state.orderDetail = cartWithLocationCoupons
          }
          setPayloadData({...transitionData.state})
          return
        }
        let oneTimeFees = (data?.add_on_code?.oneTimeFees || []).map(fee => {
          const catalogFee = catalogList.find(f => f.code === fee.code)
          if (catalogFee) {
            const currencyEntry = (catalogFee.currencies || []).find(
              c => c.currency === currency,
            )
            return {
              ...fee,
              unitAmount: currencyEntry?.unitAmount ?? fee.unitAmount,
            }
          }
          return fee
        })
        //validate coupons
        const codes = []
        let coupons = []
        // adding the plans into the coupon validation
        if (data?.plan_code.plan && data?.plan_code.plan.length > 0) {
          for (const plan of data.plan_code.plan) {
            if (!plan.code.startsWith('cc')) {
              codes.push(plan.code)
            }
          }
        }

        // adding the addons into the coupon validation
        if (data?.add_on_code?.addon && data?.add_on_code?.addon.length > 0) {
          for (const addon of data.add_on_code.addon) {
            if (!addon.code.startsWith('cc')) {
              codes.push(addon.code)
            }
          }
        }

        if (
          data?.coupon_code?.couponCodes &&
          data?.coupon_code?.couponCodes.length > 0
        ) {
          for (const coupon of data.coupon_code.couponCodes) {
            const couponDetails = await run(
              client(
                `orders/coupon/validate?couponCode=${coupon}&country=${countryCode}&planCode=${codes.join()}`,
              ),
            )
            if (couponDetails && couponDetails?.isValid) {
              coupons.push(couponDetails)
            }
          }
        }

        if (
          data?.default_incentive?.defaultIncentives &&
          data?.default_incentive?.defaultIncentives.length > 0
        ) {
          for (const coupon of data.default_incentive.defaultIncentives) {
            const couponDetails = await run(
              client(
                `orders/coupon/validate?couponCode=${coupon}&country=${countryCode}&planCode=${codes.join()}`,
              ),
            )
            if (couponDetails && couponDetails?.isValid) {
              coupons.push(couponDetails)
            }
          }
        }

        let cartObj = formatSubDetails(
          {
            plan: (data.plan_code.plan || []).map(p => ({
              ...p,
              unitAmount: undefined,
            })),
            addons: (data.add_on_code.addon || []).map(a => ({
              ...a,
              unitAmount: undefined,
            })),
            oneTimeFees,
            coupons,
          },
          subList,
          currency,
          recurlySubscriptions?.childSubscriptions,
        )

        setCartDetailObj(cartObj)
        transitionData.state.orderDetail = cartObj
        if (pendingPreviewResponse) {
          setPreviewDetails(pendingPreviewResponse)
        }
      } catch (err) {
        console.log('errror', err)
      } finally {
        setIsSummaryLoading(false)
      }
    }

    getOrderDetail()

    console.log({subscriptionList, cartDetailObj, data: transitionData.state})
    if (transitionData.state) {
      setPayloadData(transitionData.state)
    }
    // const delay = ms => new Promise(resolve => setTimeout(resolve, ms))

    const preLoadApiCall = async () => {
      try {
        // Core OOE parent-pay links persist the exact location-tagged
        // send-link payload under plan_code.corePaymentPayload. Unlike a
        // legacy KP link, every child location already exists and must remain
        // attached to its own thryvId; do not run the legacy one-parent/one-
        // location provisioning path below.
        const linkId = navData?.state?.id || navData?.state?.linkId
        const linkDetail = await client(
          `cc/order/keapClient/fetchOrder?id=${linkId}`,
        )
        let corePaymentPayload =
          linkDetail?.plan_code?.corePaymentPayload ?? null
        const isCoreLocationOrder =
          Array.isArray(corePaymentPayload?.locationDetails) &&
          corePaymentPayload.locationDetails.length > 0

        if (isCoreLocationOrder) {
          corePaymentPayload = await hydrateCorePaymentAddress(
            client,
            corePaymentPayload,
          )
          // Display-only: a failed lookup falls back to the thryvId.
          fetchLocationNames(client, corePaymentPayload).then(setLocationNames)
          const cachedPaymentKey = `coreMultiLocationPayment:${linkDetail.id}`
          let paymentOrder = null
          try {
            const cachedPaymentOrder = sessionStorage.getItem(cachedPaymentKey)
            paymentOrder = cachedPaymentOrder
              ? JSON.parse(cachedPaymentOrder)
              : null
          } catch {
            sessionStorage.removeItem(cachedPaymentKey)
          }

          if (!paymentOrder?.paymentUuid) {
            paymentOrder = await run(
              client('cc/order/send-link', {
                data: {
                  ...corePaymentPayload,
                  appUrl: window.location.origin,
                },
              }),
            )
            sessionStorage.setItem(
              cachedPaymentKey,
              JSON.stringify(paymentOrder),
            )
          }

          const accountInformation = {
            firstName: corePaymentPayload.firstName,
            lastName: corePaymentPayload.lastName,
            email: corePaymentPayload.email,
            company: corePaymentPayload.company,
            country: corePaymentPayload.businessAddress?.country,
            companyPhone: corePaymentPayload.contactPhone,
            address: corePaymentPayload.businessAddress?.street,
            city: corePaymentPayload.businessAddress?.city,
            state: corePaymentPayload.businessAddress?.state,
            postCode: corePaymentPayload.businessAddress?.postalCode,
            thryvId: corePaymentPayload.thryvId,
          }
          transitionData.state.keapPartnerLinkDetail = linkDetail
          transitionData.state.accountInformation = accountInformation
          transitionData.state.orderDetail =
            transitionData.state.orderDetail || {}
          transitionData.state.linkId = linkDetail.id
          setPayloadData(transitionData.state)
          captureOrderHeaderId.current = corePaymentPayload.orderHeaderId
          setPaymentUUID(paymentOrder.paymentUuid)
          setOrdreId(paymentOrder.orderId)
          setCountry(accountInformation.country || 'US')
          // Fetch prorated amounts from purchase-plan now that the paymentUuid is available.
          // Multi-location links have no whole-order preview (see
          // fetchLocationPreviews), so the same call also prices each location up
          // front and Due Today matches the backend before billing is saved. The
          // location previews are best effort: absent keeps the estimate.
          try {
            const previewResponse = await client(
              getPurchasePlanPath(
                paymentOrder.paymentUuid,
                corePaymentPayload.locationDetails.length > 1,
              ),
            )
            if (previewResponse?.recurlyAccountId) {
              setRecurlyAccountId(previewResponse?.recurlyAccountId)
            }
            setPreviewDetails(previewResponse)
            setLocationPreviews(previewResponse?.locationPreviews || null)
          } catch (err) {
            console.log('Error fetching purchase-plan for core payment:', err)
          }
          setIsLoading(false)
          setLoadingTimelineCompletionStatus(true)
          return
        }

        if (linkType === '1:1' && !linkDetail?.plan_code?.corePaymentPayload) {
          setIsLoading(false)
          setLoadingTimelineCompletionStatus(true)
          const response = await run(
            client(
              `orders/validate-emailaccount/${navData?.state?.custThryvId}`,
            ),
          )
          let accountInformation = {
            firstName: response.firstName,
            lastName: response.lastName,
            city: response.city,
            state: response.state,
            address: response.address,
            company: response.company,
            postCode: response.postCode,
            // generateCreateOrderPayload reads accountInformation.abn to send it in the
            // order payload; without forwarding it here it never reaches Recurly for the
            // 1:1 link flow, since this object replaces whatever order-details.js already
            // captured.
            abn: response.abn,
          }
          console.log({response})
          console.log({accountInformation})
          if (response?.thryvID) {
            thryvId.current = response?.thryvID
          }
          const codes = []

          // adding the plans into the coupon validation
          if (
            transitionData?.state?.keapPartnerLinkDetail?.plan_code.plan &&
            transitionData?.state?.keapPartnerLinkDetail?.plan_code.plan
              .length > 0
          ) {
            for (const plan of transitionData.state.keapPartnerLinkDetail
              .plan_code.plan) {
              if (!plan.code.startsWith('cc')) {
                codes.push(plan.code)
              }
            }
          }

          // adding the addons into the coupon validation
          if (
            transitionData?.state?.keapPartnerLinkDetail?.add_on_code?.addon &&
            transitionData?.state?.keapPartnerLinkDetail?.add_on_code?.addon
              .length > 0
          ) {
            for (const addon of transitionData.state.keapPartnerLinkDetail
              .add_on_code.addon) {
              if (!addon.code.startsWith('cc')) {
                codes.push(addon.code)
              }
            }
          }

          let incentiveCodes =
            transitionData?.state?.keapPartnerLinkDetail?.default_incentive?.defaultIncentives.toString() ||
            ''

          incentiveCodes = incentiveCodes.split(',')
          // let incentiveDetails
          let couponArray = []
          const couponCodes =
            transitionData?.state?.keapPartnerLinkDetail?.coupon_code
              ?.couponCodes ?? []

          if (incentiveCodes.length > 0) {
            for (let incentiveCode of incentiveCodes) {
              if (incentiveCode.length > 0) {
                let incentiveDetail = await run(
                  client(
                    `orders/coupon/validate?couponCode=${incentiveCode}&country=${countryCode}&planCode=${codes.join()}`,
                  ),
                )
                if (incentiveDetail && incentiveDetail?.isValid) {
                  couponArray.push(incentiveDetail)
                }
              }
            }
          }
          if (couponCodes) {
            for (const coupon of couponCodes) {
              const couponDetails = await run(
                client(
                  `orders/coupon/validate?couponCode=${coupon}&country=${countryCode}&planCode=${codes.join()}`,
                ),
              )
              if (couponDetails && couponDetails?.isValid) {
                couponArray.push(couponDetails)
              }
            }
          }
          // if (incentiveDetails && incentiveDetails?.isValid) {
          //   couponArray.push(incentiveDetails)
          // }
          transitionData.state.couponList = couponArray
          transitionData.state.accountInformation = accountInformation
          transitionData.state.linkType = linkType
        } else {
          setIsAccountCreationFlow(true)
          setIsFetching(false)
          const industryId = 'fpdwyu4e6ly3txw0'
          //  create acc api
          let accountInformation =
            payloadData.accountInformation ||
            transitionData.state.accountInformation
          let keapPartnerLinkDetail = transitionData.state.keapPartnerLinkDetail
          const trim = v => (typeof v === 'string' ? v.trim() : (v ?? ''))
          let accountPayload = {
            email: accountInformation.email,
            firstName: trim(accountInformation.firstName),
            lastName: trim(accountInformation.lastName),
            city: trim(accountInformation.city),
            state: trim(accountInformation.state),
            country: accountInformation.country,
            phone: accountInformation.companyPhone,
            industryId: industryId,
            mobile: accountInformation.mobile,
            address: trim(accountInformation.address),
            company: trim(accountInformation.company),
            zipCode: trim(accountInformation.postCode),
            keapID: '',
            keapAccount: false,
            xpCode: '',
            onBehalf: keapPartnerLinkDetail.keap_partner_id,
            keapPartnerId:
              keapPartnerLinkDetail.sf_partner_account_id ||
              keapPartnerLinkDetail.keap_partner_id,
          }
          if (
            (['AU', 'NZ'].includes(accountInformation.country) ||
              isInternationalTaxIdCountry(accountInformation.country)) &&
            accountInformation.abn
          ) {
            accountPayload.abn = normalizeTaxIdentifier(
              accountInformation.country,
              accountInformation.abn,
            )
          }
          let account = {}
          account = await createThryvAccount(accountPayload)

          // account = await createThryvAccountWithRetryLimit(
          //   accountPayload,
          //   RETRY_COUNT_LIMIT,
          //   DELAY_IN_MS,
          // )
          currentStep.current = currentStep.current + 1
          if (account && account.thryvId) {
            thryvId.current = account.thryvId
            sessionStorage.setItem('custThryvId', account.thryvId)
            let subDetail =
              payloadData?.orderDetail?.productSub ||
              transitionData.state.orderDetail.productSub
            accountInformation = {
              ...accountInformation,
              thryvId: account.thryvId || thryvId.current,
              industryId,
              isInvoiceBilled: false,
            }
            const captureOrderID = await postCreateAccCaptureOrderDetailTrigger(
              account,
              subDetail,
              accountInformation,
            )
            captureOrderHeaderId.current = captureOrderID
            let coupons = transitionData?.state?.couponList.map(
              item => item.code,
            )
            await sendLinkFuncWithRetryLimit(
              {
                coupons,
                accountInformation,
                keapPartnerLinkDetail,
                captureOrderID,
                subDetail,
              },
              RETRY_COUNT_LIMIT,
              DELAY_IN_MS,
            )

            currentStep.current = currentStep.current + 1
            if (currentStep.current >= 2) {
              setLoadingTimelineCompletionStatus(true)
            }
          }
          setIsLoading(false)
          setLoadingTimelineCompletionStatus(true)
        }
      } catch (err) {
        console.log(err)
        setIsLoading(false)
        setLoadingTimelineCompletionStatus(true)
        // setShowErrorAccountModal(true)
        if (!err?.handled) {
          toast.error(
            `We encountered an error while fetching data, Please contact Admin. Error: ${JSON.stringify(err)}`,
          )
        }
      }
    }

    if (preLoadStartedRef.current) return
    preLoadStartedRef.current = true
    preLoadApiCall()
  }, [])

  useEffect(() => {
    async function fetchListItem() {
      let data = await run(client('plans/5/v1/list'))
      if (data) {
        setListItem(data)
      }
    }
    async function fetchOrderHeaderId() {
      //let {linkId} = transitionData.state
      const linkId = navData?.state?.id || navData?.state?.linkId
      //console.log({transitionData, linkId: transitionData.state.linkId})
      let captureOrderHeaderDetails = await run(
        client(`keappartner/fetchCaptureOrderHeaderId/${linkId}`),
      )

      if (captureOrderHeaderDetails?.data.length > 0) {
        captureOrderHeaderId.current =
          captureOrderHeaderDetails?.data[0].order_id
      }
    }

    if (transitionData.state.linkType.includes('1:1')) {
      fetchOrderHeaderId()
    }

    fetchListItem()
  }, [transitionData?.state?.linkId])

  useEffect(() => {
    // Scroll to top when loading completes
    if (!_isLoading && !hasScrolledToTop) {
      window.scrollTo({top: 0, behavior: 'smooth'})
      setHasScrolledToTop(true)
    }
  }, [_isLoading, hasScrolledToTop])

  useEffect(() => {
    if (!isMobileViewEnable) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMobileViewEnable])

  const handleChangePaymentMethod = async method => {
    if (checkBoxesValues.agreedTerms) {
      toast.warn(
        'Please uncheck the Terms and Conditions to change your payment method.',
      )
      return
    }
    setPaymentMethod(method)
    setType(method.value)
    if (!paymentUUID || country !== 'US') return
    try {
      const locationRecurlyAccountIds =
        previewDetails?.locationRecurlyAccountIds ?? {}
      const isMultiLocation = Object.keys(locationRecurlyAccountIds).length > 0
      const accountIdsToCheck = isMultiLocation
        ? Object.values(locationRecurlyAccountIds)
        : [previewDetails?.recurlyAccountId].filter(Boolean)
      const accountIdParams = accountIdsToCheck
        .map(id => `accountIds=${encodeURIComponent(id)}`)
        .join('&')
      const couponResults = await client(
        `cc/order/fetchAccountCoupons?${accountIdParams}`,
      )
      const hasACHCoupon = coupons =>
        (coupons ?? []).some(
          c =>
            c?.coupon?.code === 'ach-discount' &&
            c?.coupon?.state === 'redeemable',
        )
      if (isMultiLocation) {
        const insertThryvIds = []
        const deleteThryvIds = []
        for (const [thryvId, recurlyAcctId] of Object.entries(
          locationRecurlyAccountIds,
        )) {
          if (hasACHCoupon(couponResults[recurlyAcctId])) {
            deleteThryvIds.push(thryvId)
          } else {
            insertThryvIds.push(thryvId)
          }
        }
        if (method.value === 'ach' && insertThryvIds.length > 0) {
          await client(`cc/order/insertACHCoupon/${paymentUUID}`, {
            method: 'POST',
            data: {locationThryvIds: insertThryvIds},
          })
        } else if (method.value !== 'ach' && deleteThryvIds.length > 0) {
          await client(`cc/order/deleteACHCoupon/${paymentUUID}`, {
            method: 'DELETE',
            data: {locationThryvIds: deleteThryvIds},
          })
        }
      } else {
        const couponResultsArray = Array.isArray(couponResults)
          ? couponResults
          : Object.values(couponResults ?? {}).flat()
        const isACHCouponAlreadyPresent = couponResultsArray.some(
          c =>
            c?.coupon?.code === 'ach-discount' &&
            c?.coupon?.state === 'redeemable',
        )
        if (!isACHCouponAlreadyPresent) {
          if (method.value === 'ach') {
            await client(`cc/order/insertACHCoupon/${paymentUUID}`, {
              method: 'POST',
            })
          } else {
            await client(`cc/order/deleteACHCoupon/${paymentUUID}`, {
              method: 'DELETE',
            })
          }
        }
      }
    } catch (err) {
      console.error('ACH coupon sync failed', err)
    }
  }

  // On page load: if an ach-discount coupon is already on the order but the
  // default payment method is card (e.g. customer refreshed after toggling ACH),
  // remove the stale coupon so a card-paying customer isn't charged a discount
  // they didn't confirm.
  useEffect(() => {
    const accountId = previewDetails?.recurlyAccountId
    if (!accountId || !paymentUUID || country !== 'US') return
    if (paymentMethod?.value === 'ach') return
    const locationRecurlyAccountIds =
      previewDetails?.locationRecurlyAccountIds ?? {}
    const isMultiLocation = Object.keys(locationRecurlyAccountIds).length > 0
    let cancelled = false
    ;(async () => {
      try {
        const accountIdsToCheck = isMultiLocation
          ? Object.values(locationRecurlyAccountIds)
          : [accountId]
        const accountIdParams = accountIdsToCheck
          .map(id => `accountIds=${encodeURIComponent(id)}`)
          .join('&')
        const couponResults = await client(
          `cc/order/fetchAccountCoupons?${accountIdParams}`,
        )
        if (cancelled) return
        const hasACHCoupon = coupons =>
          (coupons ?? []).some(
            c =>
              c?.coupon?.code === 'ach-discount' &&
              c?.coupon?.state === 'redeemable',
          )
        if (isMultiLocation) {
          // Delete only from locations that already have the ACH coupon
          const deleteThryvIds = Object.entries(locationRecurlyAccountIds)
            .filter(([, recurlyAcctId]) =>
              hasACHCoupon(couponResults[recurlyAcctId]),
            )
            .map(([thryvId]) => thryvId)
          if (deleteThryvIds.length > 0) {
            await client(`cc/order/deleteACHCoupon/${paymentUUID}`, {
              method: 'DELETE',
              data: {locationThryvIds: deleteThryvIds},
            })
          }
        } else {
          const couponResultsArray = Array.isArray(couponResults)
            ? couponResults
            : Object.values(couponResults ?? {}).flat()
          const hasAchCoupon = couponResultsArray.find(
            c =>
              c?.coupon?.code === 'ach-discount' &&
              c?.coupon?.state === 'redeemable',
          )
          if (hasAchCoupon) {
            await client(`cc/order/deleteACHCoupon/${paymentUUID}`, {
              method: 'DELETE',
            })
          }
        }
      } catch (err) {
        console.error('initial ACH coupon reconciliation failed', err)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [previewDetails?.recurlyAccountId, paymentUUID])

  async function postCreateAccCaptureOrderDetailTrigger(
    thryvAccDetail,
    subDetail,
    accountInformation,
  ) {
    let subDetailsList = [
      ...subDetail.parentSubscriptions,
      ...subDetail.childSubscriptions,
    ]
    let subLines = []
    let count = 0

    for (let i = 0; i < subDetailsList.length; i++) {
      const parentCount = count
      subLines.push(generateLineItem(subDetailsList[i], count))
      if (subDetailsList[i].addons && subDetailsList[i].addons.length > 0) {
        for (let j = 0; j < subDetailsList[i].addons.length; j++) {
          count += 1
          subLines.push(
            generateLineItem(
              subDetailsList[i].addons[j],
              count,
              parentCount,
              subDetailsList[i].code,
            ),
          )
        }
      }
      count += 1
    }
    let couponsDetails = []
    if (transitionData?.couponList) {
      couponsDetails = [...couponsDetails, ...transitionData.couponList]
    }
    if (transitionData?.defaultIncentive) {
      couponsDetails = [...couponsDetails, ...transitionData.defaultIncentive]
    }
    let createCaptureOrderPayload = generateCreateOrderPayload(
      accountInformation,
      {},
      thryvAccDetail,
      subLines,
      couponsDetails,
    )

    let captureOrderResponse = await run(
      client('ordercapture/services/createOrder', {
        method: 'POST',
        data: createCaptureOrderPayload,
      }),
    )
    // if (captureOrderResponse.Success) {
    //   setCaptureOrderId(captureOrderResponse.order_id)
    // }
    console.log(captureOrderResponse)
    return captureOrderResponse.order_id
  }
  function generateDiscountPayload(discounts = [], order_id) {
    let discountPayload = []
    discounts.map((item, index) => {
      // discountPayload.push({order_id, line_number: index + 1, discount_type: ''})
      let itemDetails = {
        order_id,
        line_number: index + 1,
        discount_percentage: item.discountPercent,
        discount_amount: item.discountAmount,
        coupon_code: item.code,
        discount_type: item.type,
        duration: item.durationAmount,
        start_date: dayjs().format('YYYY-MM-DD'),
        end_date: item.durationAmount
          ? dayjs()
              .add(item.durationAmount, item.durationUnit)
              .format('YYYY-MM-DD')
          : dayjs().format('YYYY-MM-DD'),
      }

      discountPayload.push(itemDetails)
      return item
    })
    return discountPayload
  }

  function generateCreateOrderPayload(
    accInfo,
    accDetails,
    acc,
    linesDetails,
    discounts,
  ) {
    let discountDetails = generateDiscountPayload(discounts) || []
    return {
      order_status: 'Pending',
      order_version: '5.0',
      order_flow: 'New',
      order_submitted_date: new Date(),
      order_submitted_by: 'null',
      order_completed_date: null,
      industry_id: accInfo.industryId,
      industry_description:
        'For Veterinarians, Groomers, Pet Care Services and more.',
      country_code: accInfo.country || 'US',
      account_email: accInfo.email,
      contact_first_name: accInfo.firstName,
      contact_last_name: accInfo.lastName,
      contact_phone: accInfo?.phone || accInfo?.contactPhone,
      contact_mobile: accInfo.mobile,
      contact_email: accInfo.email,
      company_name: accInfo.company,
      company_address1: accInfo.address,
      company_address2: null,
      company_city: accInfo.city,
      company_state: accInfo?.state?.value || accInfo?.state,
      company_zipcode: accInfo?.postalCode || accInfo?.postCode,
      company_phone: accInfo?.phone || accInfo?.companyPhone,
      sales_rep_id: null,
      on_behalf_of: null,
      xp_code: null,
      abn: accInfo.abn || null,
      thryv_id: acc?.thryvId,
      account_code: acc?.ccId,
      eaid: accDetails?.accountInfo?.eaid || 'keapClientPlaceHolder',
      is_invoice_billed: 0,
      is_cohort: null,
      demo_lead_id: null,
      no_service: null,
      onboarding_appt_id:
        navData?.state?.scheduleKickOffCallDetails?.callDetails?.apptId ||
        JSON.parse(
          sessionStorage.getItem('scheduleKickOffCallDetails') || 'null',
        )?.callDetails?.apptId ||
        null,
      update_to_autopay: 1,
      on_boarding_fee: 0,
      purchase_intent_1: null,
      purchase_intent_2: null,
      purchase_intent_3: null,
      sensis_site: null,
      discounts: discountDetails,
      reseller: [],
      keap_account: false,
      lines: linesDetails,
      link_id: transitionData?.state?.linkId,
    }
  }

  function generateLineItem(
    subDetail,
    index,
    parentIndex = null,
    parentSubCode = null,
  ) {
    let isCenter = listItem.centers.find(item => item.code === subDetail.code)
    let isOneTimeFee = listItem.oneTimeFees?.find(
      item => item.code === subDetail.code,
    )
    let isApp = listItem.apps.find(item => item.code === subDetail.code)
    let plansv5_id = ''
    let saas_product_category = ''
    let saas_product_type = ''
    let recurly_subscription_id = null
    let unit_cost = 0
    let quantity = 1
    let total_cost = unit_cost * quantity
    let parent_line_number =
      parentIndex !== null ? parentIndex + 1 : parentIndex

    if (isApp) {
      saas_product_category = 'App'
      saas_product_type = 'addon'
      plansv5_id = isApp.id
    } else if (isCenter) {
      saas_product_category = 'Center'
      saas_product_type = 'main'
      plansv5_id = isCenter.id
      unit_cost = isCenter?.unitAmount || 0
      quantity = isCenter?.quantity || 1
      total_cost = unit_cost * quantity
    } else if (isOneTimeFee) {
      saas_product_category = 'OneTimeFee'
      saas_product_type = 'oneTimeFee'
      plansv5_id = isOneTimeFee.id
      unit_cost = isCenter?.unitAmount || 0
      quantity = isCenter?.quantity || 1
      total_cost = unit_cost * quantity
    } else if (subDetail.code === 'appmarket-5-thryv-m2m') {
      saas_product_category = 'App'
      saas_product_type = 'addon'
      plansv5_id = 'wo6lpqm9wsxn'
      unit_cost = isApp?.unitAmount || 0
      quantity = isApp?.quantity || 1
      total_cost = unit_cost * quantity
    }
    if (parentSubCode) {
      saas_product_category = 'App'
      saas_product_type = 'addon'
      if (parentSubCode === 'appmarket-5-thryv-m2m') {
        if (!plansv5_id) {
          plansv5_id = 'wo6lpqm9wsxn'
        } else {
          parent_line_number = null
        }
      } else {
        plansv5_id =
          [...listItem.centers, ...listItem.apps, ...listItem.oneTimeFees].find(
            item => item.code === parentSubCode,
          )?.id || 'wo6lpqm9wsxn'
      }
    }
    return {
      line_number: index + 1,
      parent_line_number,
      plansv5_id,
      action_code: 'New',
      order_line_status: 'InProgress',
      saas_product_category,
      saas_product_type,
      saas_product_code: subDetail.code,
      saas_instance_id: null,
      recurly_subscription_id,
      total_cost,
      unit_cost,
      quantity,
      start_date: new Date(),
      end_date: null,
    }
  }

  async function fetchAccountDetails() {
    let accDetails
    const custThryvId =
      navData?.state?.custThryvId || navData?.state?.existingThryvId
    if (custThryvId) {
      accDetails = await run(
        client(`orders/validate-emailaccount/${custThryvId}`),
      )
    }
    return accDetails
  }

  // Covers the observed prod latency (address/timezone geocoding took ~2min for one of the
  // two racing requests) while still self-healing after a page reload abandons an in-flight
  // attempt without ever reaching the `finally` below.
  const ACCOUNT_CREATION_IN_FLIGHT_TTL_MS = 3 * 60 * 1000

  async function createThryvAccount(accountPayload) {
    let account = {}
    let accountDetail = sessionStorage.getItem('accountDetail')

    if (!accountDetail) {
      const inFlightAt = Number(
        sessionStorage.getItem('accountCreationInFlight') || 0,
      )
      if (
        inFlightAt &&
        Date.now() - inFlightAt < ACCOUNT_CREATION_IN_FLIGHT_TTL_MS
      ) {
        // A remount (e.g. this page reloading mid-request) raced an account-creation
        // attempt that's still plausibly in flight — do not fire a second one (this is
        // the exact double-POST that produced a duplicate Recurly account in prod).
        currentErrorStep.current = 'postAccountCreationRetries'
        showErrorAccountModal.current = true
        return account
      }
      sessionStorage.setItem('accountCreationInFlight', String(Date.now()))

      try {
        try {
          accountDetail = await fetchAccountDetails()
        } catch (err) {
          console.log(err)
        }
        if (
          accountDetail &&
          (accountDetail?.thryvID ?? accountDetail?.thryvId)
        ) {
          accountDetail['thryvId'] =
            accountDetail.thryvID ?? accountDetail?.thryvId
          accountDetail['ccId'] =
            accountDetail.parentSubscriptions[0]?.account?.code ||
            accountDetail.thryvID
          return accountDetail
        }

        const flowType = transitionData?.state?.keapPartnerLinkDetail
          ?.influencer_name
          ? 'influencerops'
          : undefined
        const address = {
          street: accountPayload.address,
          city: accountPayload.city,
          state: accountPayload.state,
          zipCode: accountPayload.zipCode,
          country: accountPayload.country,
        }

        // Resume from a prior attempt that created the parent but never finished the
        // location call, instead of re-POSTing /multi-location/parent (which would just
        // hit a clean 409 for the same email on a wasted retry).
        let parentResult
        const persistedParent = sessionStorage.getItem('accountDetailParent')
        if (persistedParent) {
          parentResult = JSON.parse(persistedParent)
        } else {
          const parentPayload = {
            email: accountPayload.email,
            firstName: accountPayload.firstName,
            lastName: accountPayload.lastName,
            company: accountPayload.company,
            phone: accountPayload.phone,
            country: accountPayload.country,
            address,
            ...(flowType && {flowType}),
          }
          parentResult = await run(
            client('multi-location/parent', {data: parentPayload}),
          )
          sessionStorage.setItem(
            'accountDetailParent',
            JSON.stringify(parentResult),
          )
        }

        const locationPayload = {
          parentOrgId: parentResult.parentOrgId,
          parentSignupId: parentResult.signupId,
          parentCcId: parentResult.ccId,
          parentRecurlyAccountId: parentResult.recurlyAccountId,
          provisionRecordId: parentResult.provisionRecordId,
          email: accountPayload.email,
          firstName: accountPayload.firstName,
          lastName: accountPayload.lastName,
          company: accountPayload.company,
          phone: accountPayload.phone,
          country: accountPayload.country,
          // Reuse the parent call's derived timezone — avoids re-geocoding the same address.
          timeZone: parentResult.timeZone,
          address,
          ...(flowType && {flowType}),
        }
        const locationResult = await run(
          client('multi-location/location', {data: locationPayload}),
        )

        // api-client resolves any 2xx (including 207) as success — a partial failure must
        // be detected explicitly rather than relying on a thrown/caught error.
        if (locationResult.childProvisionFailed) {
          throw new Error(
            locationResult.error || 'Account provisioning incomplete',
          )
        }

        account = {
          thryvId: locationResult.thryvId,
          ccId: locationResult.ccId,
        }
        sessionStorage.setItem('accountDetail', JSON.stringify(account))
        sessionStorage.removeItem('accountDetailParent')
      } catch (err) {
        console.log(err)
        setLoadingTimelineCompletionStatus(true)
        if (err?.message) {
          navigate('/client-order', {
            state: {
              ...transitionData.state,
              accountValidationError: err.message,
            },
          })
          const handledError = new Error(err.message)
          handledError.handled = true
          throw handledError
        }
        currentErrorStep.current = 'postAccountCreationRetries'
        showErrorAccountModal.current = true
        // setLoadingTimelineCompletionStatus(true)
        // return
        //throw err
      } finally {
        sessionStorage.removeItem('accountCreationInFlight')
      }
    } else {
      setLoadingTimelineCompletionStatus(true)
      account = JSON.parse(accountDetail)
    }
    return account
  }

  // async function createThryvAccountWithRetryLimit(
  //   accountPayload,
  //   maxRetries = 5,
  //   delay = 1000,
  // ) {
  //   let retries = 0

  //   while (retries < maxRetries) {
  //     try {
  //       const account = await createThryvAccount(accountPayload)
  //       return account
  //     } catch (error) {
  //       if (retries === 0) {
  //         showErrorAccountModal.current = true
  //       }
  //       retries += 1
  //       console.log(`Account creation attempt ${retries} failed:`, error)
  //       if (retries >= maxRetries) {
  //         console.log(
  //           `Max retries (${maxRetries}) reached for account creation`,
  //         )
  //         currentErrorStep.current = 'postAccountCreationRetries'
  //         setLoadingTimelineCompletionStatus(true)
  //         showErrorAccountModal.current = true
  //       }
  //       // Wait before retrying
  //       await new Promise(resolve => setTimeout(resolve, delay))
  //       if (retries === 1 && showErrorAccountModal.current) {
  //         showErrorAccountModal.current = false
  //       }
  //     }
  //   }
  // }
  async function sendLinkTrigger({
    coupons,
    // delay,
    accountInformation,
    keapPartnerLinkDetail,
    captureOrderID,
    subDetail,
  }) {
    // try {
    let updatedSubDetails = formatInCodeQuantityStructure(subDetail)
    let sendLinkPayloadData = {
      ...accountInformation,
      ...updatedSubDetails,
      coupons: coupons,
      overrideRepId: keapPartnerLinkDetail.sf_partner_account_id,
      behalfRepId: keapPartnerLinkDetail.keap_partner_id,
      orderHeaderId: captureOrderID || captureOrderHeaderId.current,
    }
    const seoProposalId = navData?.state?.seoProposalId
    if (seoProposalId) {
      sendLinkPayloadData.seoProposalId = seoProposalId
    }
    const slpShareLinkId = transitionData?.state?.slpShareLinkId
    if (slpShareLinkId) {
      sendLinkPayloadData.slp_share_link_id = slpShareLinkId
    }
    if (
      (['AU', 'NZ'].includes(accountInformation.country) ||
        isInternationalTaxIdCountry(accountInformation.country)) &&
      accountInformation.abn
    ) {
      sendLinkPayloadData.abn = normalizeTaxIdentifier(
        accountInformation.country,
        accountInformation.abn,
      )
      if (!sendLinkPayloadData.abn.length) delete sendLinkPayloadData.abn
    }
    if (navData?.state?.scheduleKickOffCallDetails) {
      sendLinkPayloadData['onboardingApptId'] =
        navData?.state?.scheduleKickOffCallDetails?.callDetails?.apptId || ''
    }
    // scheduled appointment table details
    if (navData?.state?.scheduleKickOffCallDetails) {
      await captureAppointment(
        navData?.state?.scheduleKickOffCallDetails?.callDetails,
        captureOrderID,
      )
    }
    if (navData?.state?.scheduleCreativeCallDetails) {
      await captureAppointment(
        navData?.state?.scheduleCreativeCallDetails?.callDetails,
        captureOrderID,
      )
    }
    const result = await run(
      client('cc/order/send-link', {
        data: sendLinkPayloadData,
      }),
    )
    if (captureOrderID) {
      await run(
        client('ordercapture/services/updateOrderStatus', {
          method: 'PUT',
          data: {
            order_id: Number(captureOrderID),
            cc_order_id: result.orderId,
            order_status: 'Submitted',
            userEmailId:
              transitionData?.state?.keapPartnerLinkDetail?.partner_email,
            payment_uuid: result?.paymentUuid,
          },
        }),
      )
    }

    if (result.message === 'Order created') {
      setPaymentUUID(result.paymentUuid)
      setOrdreId(result.orderId)
      // Fetch schedule details for influencer links
      if (
        usesInfluencerScheduling(
          transitionData?.state?.keapPartnerLinkDetail,
          transitionData?.state?.linkType,
        )
      ) {
        try {
          const scheduleData = await client(
            `keappartner/fetchScheduleDetails/${captureOrderID}`,
          )
          if (scheduleData.success) {
            setScheduleDetails(scheduleData.data)
          }
        } catch (err) {
          console.log(err)
          toast.error(
            `We encountered an error while fetching schedule data, Please contact Admin. Error: ${JSON.stringify(err)}`,
          )
          setLoadingTimelineCompletionStatus(true)
          setIsLoading(false)
        }
      }
    }
  }
  // create function which will re for atleast 5 times before send the alert
  async function sendLinkFuncWithRetryLimit(
    {
      coupons,
      // delay,
      accountInformation,
      keapPartnerLinkDetail,
      captureOrderID,
      subDetail,
    },
    maxRetries = 5,
    delay = 1000,
  ) {
    let attempts = 0
    while (attempts < maxRetries) {
      try {
        const result = await sendLinkTrigger({
          coupons,
          // delay,
          accountInformation,
          keapPartnerLinkDetail,
          captureOrderID,
          subDetail,
        })
        return result
      } catch (error) {
        attempts++
        console.log(`Attempt ${attempts} failed:`, error)

        if (attempts >= maxRetries) {
          console.log(error)
          // setShowErrorAccountModal(true)
          showErrorAccountModal.current = true
          return
        }
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
  }
  // Function to complete the purchase flow
  const completePurchaseFlow = async () => {
    setIsLoading(true)
    let purchaseData
    try {
      if (captureOrderHeaderId.current) {
        const onboardingApptId =
          navData?.state?.scheduleKickOffCallDetails?.callDetails?.apptId ||
          JSON.parse(
            sessionStorage.getItem('scheduleKickOffCallDetails') || 'null',
          )?.callDetails?.apptId ||
          undefined
        if (onboardingApptId) {
          await run(
            client('ordercapture/services/updateOnboardingApptId', {
              method: 'PUT',
              data: {
                order_id: Number(captureOrderHeaderId.current),
                onboarding_appt_id: onboardingApptId,
              },
            }),
          )
        }
      }
      purchaseData = await run(
        client('cc/order/process-purchase-asynchronous', {
          data: {
            orderId: captureOrderHeaderId.current?.toString(),
            appUrl: window.location.origin,
          },
        }),
      )
      // FULSAS-12190: process-purchase always returns 200, with any
      // appointment-booking failures (onboarding/creative/TSS call) reported
      // separately in `errors` - the subscription itself already succeeded
      // by this point, so this is scheduling friction, not a purchase
      // failure. Let the client know without alarming them, and don't block
      // the rest of the flow.
      const appointmentErrorSteps = [
        'onboarding',
        'creative',
        'thryv success services',
      ]
      if (
        purchaseData?.errors?.some(({step}) =>
          appointmentErrorSteps.includes(step),
        )
      ) {
        toast.warn(
          'Your purchase is complete! We hit a snag scheduling your call — our team will follow up to confirm a time.',
        )
      }
      // 'update subscription' errors mean an add-on/plan change was rejected
      // by Recurly (most commonly a declined card on the add-on charge) and
      // was never applied to the account, unlike the appointment-booking
      // errors above. This must surface as a hard error, not a soft toast,
      // so the client knows to fix payment and doesn't assume everything
      // they ordered was provisioned.
      if (
        purchaseData?.errors?.some(({step}) => step === 'update subscription')
      ) {
        toast.error(
          'Your purchase went through, but one or more add-ons could not be added because your card was declined for that charge. Please update your payment method and contact support to finish adding them to your account.',
        )
      }
      if (transitionData.state.linkType.includes('1:1')) {
        await run(
          client(`keappartner/updateKeapPartnerLinkStatus`, {
            data: {linkId: transitionData.state.linkId, status: 'Completed'},
            method: 'PUT',
          }),
        )
        let orderHeaderIdRes = await client(
          `ordercapture/services/getOrderDetailsByLinkId/${transitionData.state.linkId}`,
        )
        if (
          orderHeaderIdRes &&
          orderHeaderIdRes['success'] &&
          orderHeaderIdRes['response']['data'] &&
          orderHeaderIdRes['response']['data'].length > 0
        ) {
          const orderHeaderId = orderHeaderIdRes['response']['data'][0].order_id
          captureOrderHeaderId.current = orderHeaderId
        }

        const kickoffAppt =
          navData?.state?.scheduleKickOffCallDetails?.callDetails ||
          JSON.parse(
            sessionStorage.getItem('scheduleKickOffCallDetails') || 'null',
          )?.callDetails
        if (captureOrderHeaderId.current && kickoffAppt?.apptId) {
          await captureAppointment(kickoffAppt, captureOrderHeaderId.current)
        }
      }
    } catch (err) {
      toast.error(err.message)
      setIsLoading(false)
      return undefined
    }

    let data = {
      linkId: transitionData.state.linkId,
      currentScreen: 'paymentInfo',
      baseUrl: window.location.href,
    }
    await run(
      client('keappartner/updatePartnerActivity', {
        data,
        method: 'PUT',
      }),
    )
    // let orderDetails = {
    //   clientName:
    //     transitionData.state.accountInformation.firstName +
    //     ' ' +
    //     transitionData.state.accountInformation.lastName,
    //   clientEmailId: navData?.state?.custEmail
    //     ? navData?.state?.custEmail
    //     : transitionData?.state?.accountInformation?.email,
    //   baseUrl: window.location.href,
    //   planName:
    //     transitionData?.state?.orderDetail?.plans
    //       .filter(item => !NON_ADDED_PLANS.includes(item.code))
    //       .map(item => item.name) || [],
    //   companyName: transitionData.state.accountInformation.company,
    //   linkId: transitionData.state.linkId,
    //   ccOrderId: orderId,
    //   thryvId: thryvId.current,
    // }
    // await run(
    //   client('keappartner/sendPurchseOrderDetails', {
    //     data: orderDetails,
    //     method: 'POST',
    //   }),
    // )
    // Core (Account Setup wizard) links never load the booked call before this
    // point, so the confirmation page would not list it — whether the call was
    // booked here (Influencer) or by the rep in ooe-app (KeapPartner /
    // PartnerOps 1:1). The lookup returns nothing for an order with no call.
    // The purchase has already succeeded, so a failed lookup only means no
    // appointment card.
    let confirmationScheduleDetails = scheduleDetails
    if (captureOrderHeaderId.current) {
      try {
        const scheduleData = await client(
          `keappartner/fetchScheduleDetails/${captureOrderHeaderId.current}`,
        )
        if (scheduleData?.success && scheduleData.data?.apptDetails?.length) {
          confirmationScheduleDetails = scheduleData.data
        }
      } catch (err) {
        console.log('Error fetching schedule details', err)
      }
    }
    toast.success('Order was successfully created.')
    setIsLoading(false)
    if (purchaseData) {
      sessionStorage.removeItem('accountDetail')
      sessionStorage.removeItem('scheduleKickOffCallDetails')
      sessionStorage.removeItem('scheduleCreativeCallDetails')
      navigate('/client-acknowledgement', {
        state: {
          name: transitionData.state.accountInformation.firstName,
          orderId,
          linkId: transitionData.state.linkId,
          scheduleDetails: confirmationScheduleDetails,
          influencer_name:
            transitionData?.state?.keapPartnerLinkDetail?.influencer_name,
          kickoffCallLabel: transitionData.state.kickoffCallLabel,
        },
      })
    }
  }

  const calculateTaxes = async paymentResponse => {
    try {
      setCalculatingTaxes(true)
      setIsLoading(true)
      if (country !== 'AU') {
        toast.info(
          'Tax is being calculated based on your business address. The final amount will be updated shortly.',
        )
      }
      const data = getBillingSaveData(paymentResponse)
      let url = 'cc/order/save-billing'
      const result = await client(url, {data})
      if (result?.tax != null) {
        setBillingTax(result.tax)
      }
      if (paymentUUID) {
        try {
          // Multi-location core links have no whole-order preview (see
          // fetchLocationPreviews); the same call prices each location instead.
          const isMultiLocationCore =
            transitionData.state.orderDetail?.isCoreLink &&
            transitionData.state.orderDetail?.locations?.length > 1
          let previewResponse = await client(
            getPurchasePlanPath(paymentUUID, isMultiLocationCore),
          )
          // console.log({previewResponse})
          setPreviewDetails(previewResponse)
          if (isMultiLocationCore) {
            setLocationPreviews(previewResponse?.locationPreviews || null)
          }
        } catch (err) {
          console.log(err)
          return undefined
        }
      }
      setTaxCalculateStatus(true)
      setCalculatingTaxes(false)
      setIsLoading(false)
    } catch (error) {
      if (error.errors) {
        error.errors.forEach(({message}) => toast.error(message))
      } else {
        toast.error(error.message)
      }
      setCalculatingTaxes(false)
      setIsLoading(false)
    }
  }

  // Hand each location its backend preview. Idempotent: skipped once every
  // location already holds it, so replacing the cart cannot loop.
  useEffect(() => {
    const cart = transitionData.state?.orderDetail
    if (!locationPreviews || !cart?.locations) return
    if (
      cart.locations.every(
        location =>
          location.preview === locationPreviews[location.locationThryvId],
      )
    ) {
      return
    }
    const withPreviews = attachLocationPreviews(cart, locationPreviews)
    if (withPreviews === cart) return
    transitionData.state.orderDetail = withPreviews
    setCartDetailObj(withPreviews)
  }, [locationPreviews, cartDetailObj]) // eslint-disable-line react-hooks/exhaustive-deps

  const handlePaymentDetailContinue = async () => {
    if (!checkBoxesValues.agreedTerms) {
      toast.warn('Please agree to the Terms and Conditions to continue.')
      return null
    }
    setIsLoading(true)

    await completePurchaseFlow()
  }
  const getBillingSaveData = paymentResponse => {
    return {
      orderUuid: paymentUUID,
      billingMethod: paymentMethod?.value === 'card' ? 'CC' : 'ACH',
      recurlyToken: paymentResponse ? paymentResponse.id : undefined,
    }
  }

  async function captureAppointment(data, orderHeaderId) {
    console.log('new', data)
    const createAppointModel = []
    const appointmentObj = {
      order_id: orderHeaderId,
      schedule_status: 'Scheduled',
      appointment_type: data.appointment_type,
      timezone: data.timeZone,
      appt_id: data.apptId,
      date: data.thryvBookingDate.split('T')[0],
      time: data.thryvBookingDate.split('T')[1],
      title: 'Meeting Connect',
      duration: 30,
      service_id: data.serviceId,
      staff_id: data.staffId,
      interaction: 'call Interaction',
      zoomURL: 'https://www.zoom.com',
      create_user: null,
      update_user: null,
    }
    createAppointModel.push(appointmentObj)
    try {
      let result
      let url = 'ordercapture/services/createAppointment'
      result = await kP11LinkService.captureAppoinmentDetail(
        client,
        url,
        createAppointModel,
      )
      console.log(result)
    } catch (error) {
      if (error.status !== 400) {
        console.log('error in captureAppointment', error)
      }
      if (error.errors) {
        console.log('error in captureAppointment', error)
      }
    }
  }

  function getLoadingMessage({
    calculatingTaxes,
    isVerifying,
    isFetching,
    isFetchingRecurly,
    isAus,
    isCVCVerified,
  }) {
    if (isVerifying) {
      return 'Verifying CVC'
    }
    if (calculatingTaxes) {
      if (isAus && isCVCVerified) return 'Verifying CVC'
      return isAus ? 'Validating Details' : 'Calculating your Taxes & Fees'
    }
    if (isFetching) {
      return 'Fetching card on file'
    }
    if (isFetchingRecurly) {
      return 'Processing'
    }
    return 'Processing'
  }

  React.useEffect(() => {
    if (isAgreeCheckboxDisabled) {
      clearRecurlyToken()
      return undefined
    }
    if (!recurlyToken && !isCVCVerified) return undefined
    if (is5Order) {
      // calculateTaxesCC(recurlyToken)
      calculateTaxes(recurlyToken)
    } else {
      calculateTaxes(recurlyToken)
    }
  }, [is5Order, recurlyToken, isCVCVerified, isAgreeCheckboxDisabled])

  React.useEffect(() => {
    if (isAgreeCheckboxDisabled && currentStep.current <= 3) {
      dispatch(clearRecurlyTokenData())
      return undefined
    }
    if (!recurlyTokenDetail) return undefined
    if (is5Order) {
      // calculateTaxesCC(recurlyToken)
      calculateTaxes(recurlyTokenDetail)
    } else {
      calculateTaxes(recurlyTokenDetail)
    }
  }, [is5Order, recurlyTokenDetail, isAgreeCheckboxDisabled])

  const isAusCvcVerify = isVerifying && country === 'AU'
  const displayLoading = (calculatingTaxes || isAusCvcVerify) && _isLoading
  const loadingMessage = getLoadingMessage({
    isFetching,
    isFetchingRecurly: isFetching,
    isVerifying,
    calculatingTaxes,
    isAus: country === 'AU',
    isCVCVerified,
  })
  return (
    <>
      {isLoading && <AppSpinner />}
      {displayLoading && (
        <div style={{position: 'relative', zIndex: 999}}>
          <OverlayLoader
            is5Order={is5Order}
            className="d-flex justify-content-center align-items-center w-100 position-fixed"
          >
            {calculatingTaxes ? (
              <div
                style={{
                  backgroundColor: 'white',
                  borderRadius: '50px',
                  paddingTop: '48px',
                  paddingInline: '28px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: '240px',
                }}
              >
                <img
                  src={calculatingTaxesGif}
                  alt="Calculating taxes"
                  style={{
                    maxWidth: '141px',
                  }}
                />
                <ParagraphText
                  variant="sm"
                  className="font-weight-bold mt-4"
                  style={{
                    color: '#231F20',
                    whiteSpace: 'nowrap',
                    fontSize: '13px',
                  }}
                >
                  {loadingMessage}
                </ParagraphText>
                <img
                  src={thryvPeek}
                  alt="Thryver peeking"
                  style={{
                    width: '100%',
                    maxWidth: '141px',
                    transform: 'translateY(10px)',
                  }}
                />
              </div>
            ) : (
              <>
                <AppSpinner />
                <ParagraphText
                  variant="lg"
                  color="thryv-white-50"
                  className="font-weight-bold mt-4"
                >
                  {loadingMessage}
                </ParagraphText>
              </>
            )}
          </OverlayLoader>
        </div>
      )}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100vh',
          background: 'linear-gradient(to bottom, #171d37, #6474a9)',
          zIndex: -1,
        }}
      />
      {!isMobileViewEnable && (
        <ThryvAndKeapHeader
          businessName={payloadData?.accountInformation?.company}
          linkExpiryInMilliSec={linkExpiryInMinutes}
          totalAmount={(() => {
            if (isSummaryBusy) return ''
            if (
              displayPreview?.total != null &&
              (isTaxCalculated || displayPreview.tax > 0)
            ) {
              return `$${Number(Math.max(0, displayPreview.total)).toFixed(2)}`
            }
            const t = computeTabTotal({
              paymentDetailType: 'today',
              cartDetailObj: transitionData.state.orderDetail,
              previewDetails,
              finalBillingAmountDetails,
              couponList: transitionData.state.couponList?.length
                ? transitionData.state.couponList
                : (previewDetails?.coupons ?? []),
              country,
            })
            return t != null
              ? `$${Number(t).toFixed(2)}${country === 'AU' ? '' : ' + Tax'}`
              : undefined
          })()}
        />
      )}
      {!isMobileViewEnable && isUsdOnlyCountry(country) && (
        <div
          role="status"
          style={{
            margin: '12px 24px 0',
            padding: '12px 16px',
            borderRadius: '8px',
            background: '#FFF4D6',
            color: '#3D2A00',
            fontSize: '14px',
          }}
        >
          Currency conversion fees may apply from your bank or credit card
          company since transactions are processed in USD.
        </div>
      )}
      {isMobileViewEnable ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <MobilePaymentHeader
            businessName={payloadData?.accountInformation?.company}
            linkExpiryInMilliSec={linkExpiryInMinutes}
            totalAmount={(() => {
              if (isSummaryBusy) return ''
              if (
                displayPreview?.total != null &&
                (isTaxCalculated || displayPreview.tax > 0)
              ) {
                return `$${Number(Math.max(0, displayPreview.total)).toFixed(
                  2,
                )}`
              }
              const t = computeTabTotal({
                paymentDetailType: 'today',
                cartDetailObj: transitionData.state.orderDetail,
                previewDetails,
                finalBillingAmountDetails,
                couponList: transitionData.state.couponList?.length
                  ? transitionData.state.couponList
                  : (previewDetails?.coupons ?? []),
                country,
              })
              return t != null ? `$${Number(t).toFixed(2)}` : undefined
            })()}
            hasTax={
              !(
                displayPreview?.total != null &&
                (isTaxCalculated || displayPreview.tax > 0)
              ) && country !== 'AU'
            }
            onBack={mobileStep === 2 ? () => setMobileStep(1) : undefined}
          />
          {isUsdOnlyCountry(country) && (
            <div
              role="status"
              style={{
                padding: '12px 16px',
                background: '#FFF4D6',
                color: '#3D2A00',
                fontSize: '14px',
                flexShrink: 0,
              }}
            >
              Currency conversion fees may apply from your bank or credit card
              company since transactions are processed in USD.
            </div>
          )}
          <div
            className={!_isLoading ? '' : 'overlay--style'}
            style={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              paddingInline: '12px',
              gap: '12px',
              boxSizing: 'border-box',
              minHeight: 0,
            }}
          >
            <div style={{display: mobileStep === 1 ? 'contents' : 'none'}}>
              {/* CATALOG STEP */}
              <div
                style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  overflow: 'hidden',
                }}
              >
                {/* TABS HEADER */}
                <div style={{flexShrink: 0, paddingInline: '10px'}}>
                  <GradientTabs
                    activeTab={activeRightTab}
                    onChange={setActiveRightTab}
                    variant="payment"
                  >
                    <GradientTab id="today">
                      <div className="label">
                        <p
                          className={`tab-label-title${activeRightTab === 'today' ? ' active' : ''}`}
                        >
                          Due Today{' '}
                          <span>
                            (
                            {(() => {
                              const alreadyAddedCodes = new Set(
                                (cartDetailObj?.plans || [])
                                  .filter(p => p.isAlreadyAdded)
                                  .map(p => p.code)
                                  .filter(Boolean),
                              )
                              // Core links count from the cart (same rule as the
                              // rows); the preview only lists today's charges.
                              const coreCount = getCoreTabItemCount(
                                cartDetailObj,
                                'today',
                              )
                              if (coreCount !== null) return coreCount
                              return previewDetails?.paymentPreview
                                ? [
                                    ...(previewDetails.paymentPreview.apps ||
                                      []),
                                    ...(previewDetails.paymentPreview.centers ||
                                      []),
                                    ...(previewDetails.paymentPreview
                                      .oneTimes || []),
                                  ].filter(
                                    item =>
                                      !alreadyAddedCodes.has(
                                        item.planCode || item.code,
                                      ),
                                  ).length
                                : (getCoreTabItemCount(
                                    cartDetailObj,
                                    'today',
                                  ) ??
                                    (cartDetailObj?.plans || []).filter(
                                      p =>
                                        !NON_ADDED_PLANS.includes(p.code) &&
                                        !p.isAlreadyAdded,
                                    ).length)
                            })()}{' '}
                            Items)
                          </span>
                        </p>
                      </div>
                      <p
                        className={`tab-price${activeRightTab === 'today' ? ' active' : ''}`}
                      >
                        {(() => {
                          if (isSummaryBusy) return ''
                          if (
                            displayPreview?.total != null &&
                            (isTaxCalculated || displayPreview.tax > 0)
                          ) {
                            return formatPrice({
                              amount: Math.max(0, displayPreview.total),
                              country,
                            })
                          }
                          const t = computeTabTotal({
                            paymentDetailType: 'today',
                            cartDetailObj: transitionData.state.orderDetail,
                            previewDetails,
                            finalBillingAmountDetails,
                            couponList: transitionData.state.couponList?.length
                              ? transitionData.state.couponList
                              : (previewDetails?.coupons ?? []),
                            country,
                          })
                          if (t == null) return ''
                          const showPlusTax =
                            country !== 'AU' &&
                            !(isTaxCalculated || displayPreview?.tax > 0)
                          return showPlusTax
                            ? `${formatPrice({amount: t, country})} + Tax`
                            : formatPrice({amount: t, country})
                        })()}
                      </p>
                    </GradientTab>
                    <GradientTab id="monthly">
                      <div className="label blue">
                        <p
                          className={`tab-label-title${activeRightTab === 'monthly' ? ' active' : ''}`}
                        >
                          {(() => {
                            const annualT = computeTabTotal({
                              paymentDetailType: 'annual',
                              cartDetailObj: transitionData.state.orderDetail,
                              country,
                            })
                            const monthlyT = computeTabTotal({
                              paymentDetailType: 'monthly',
                              cartDetailObj: transitionData.state.orderDetail,
                              country,
                            })
                            return annualT > 0 && monthlyT === 0
                              ? 'Due Annually'
                              : 'Due Monthly'
                          })()}{' '}
                          <span>
                            (
                            {(() => {
                              const annualT = computeTabTotal({
                                paymentDetailType: 'annual',
                                cartDetailObj: transitionData.state.orderDetail,
                                country,
                              })
                              const monthlyT = computeTabTotal({
                                paymentDetailType: 'monthly',
                                cartDetailObj: transitionData.state.orderDetail,
                                country,
                              })
                              const isAnnualView = annualT > 0 && monthlyT === 0
                              const isAnnualItem = p =>
                                String(p.code || '')
                                  .toLowerCase()
                                  .includes('annual') ||
                                String(p.name || '')
                                  .toLowerCase()
                                  .includes('annual') ||
                                String(p.name || '')
                                  .toLowerCase()
                                  .includes('yearly')
                              const coreMonthlyCount = getCoreTabItemCount(
                                cartDetailObj,
                                isAnnualView ? 'annual' : 'monthly',
                              )
                              if (coreMonthlyCount !== null) {
                                return coreMonthlyCount
                              }
                              if (previewDetails?.paymentPreview) {
                                return [
                                  ...(previewDetails.paymentPreview.apps || []),
                                  ...(previewDetails.paymentPreview.centers ||
                                    []),
                                ].length
                              }
                              const coreCount = getCoreTabItemCount(
                                cartDetailObj,
                                isAnnualView ? 'annual' : 'monthly',
                              )
                              if (coreCount !== null) return coreCount
                              return (cartDetailObj?.plans || []).filter(
                                p =>
                                  !NON_ADDED_PLANS.includes(p.code) &&
                                  p.prodType !== 'OneTimeFees' &&
                                  p.prodType !== 'oneTimeFee' &&
                                  p.section !== 'oneTimeFees' &&
                                  p.groupKey !== 'oneTimeFees' &&
                                  (isAnnualView
                                    ? isAnnualItem(p)
                                    : !isAnnualItem(p)),
                              ).length
                            })()}{' '}
                            Items)
                          </span>
                        </p>
                      </div>
                      <p
                        className={`tab-price${activeRightTab === 'monthly' ? ' active' : ''}`}
                      >
                        {(() => {
                          if (isSummaryBusy) return ''
                          const annualT = computeTabTotal({
                            paymentDetailType: 'annual',
                            cartDetailObj: transitionData.state.orderDetail,
                            country,
                          })
                          const monthlyT = computeTabTotal({
                            paymentDetailType: 'monthly',
                            cartDetailObj: transitionData.state.orderDetail,
                            country,
                          })
                          const t =
                            annualT > 0 && monthlyT === 0 ? annualT : monthlyT
                          if (t == null) return ''
                          if (country === 'AU') {
                            return (
                              <>
                                {formatPrice({amount: t, country})}
                                <span style={{color: '#FF5000'}}>*</span>
                              </>
                            )
                          }
                          return (
                            <>
                              {`${formatPrice({amount: t, country})} + Tax`}
                              {country !== 'NZ' && (
                                <span style={{color: '#FF5000'}}>*</span>
                              )}
                            </>
                          )
                        })()}
                      </p>
                    </GradientTab>
                  </GradientTabs>
                </div>
                {/* SCROLLABLE PRODUCTS */}
                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    padding: '10px',
                    scrollbarGutter: 'stable',
                    background: '#DDE1EA',
                    borderRadius: '10px',
                    boxShadow: '0px 0px 22px 0px #00000040 inset',
                  }}
                >
                  {isSummaryBusy ? (
                    <SummarySpinner />
                  ) : activeRightTab === 'today' ? (
                    <KCPPaymentDetails
                      country={country}
                      locationNames={locationNames}
                      paymentState={dataJSON}
                      paymentDetailType="today"
                      cartDetailObj={transitionData.state.orderDetail}
                      finalBillingAmountDetails={finalBillingAmountDetails}
                      currentScreen={'paymentInfo'}
                      couponList={
                        transitionData.state.couponList?.length
                          ? transitionData.state.couponList
                          : (previewDetails?.coupons ?? [])
                      }
                      previewDetails={previewDetails}
                      preselectedSubData={preselectedSubData}
                      hideHeading
                      isTaxCalculated={isTaxCalculated}
                    />
                  ) : (
                    <KCPPaymentDetails
                      country={country}
                      locationNames={locationNames}
                      paymentState={dataJSON}
                      cartDetailObj={transitionData.state.orderDetail}
                      paymentDetailType="monthly"
                      currentScreen={'paymentInfo'}
                      preselectedSubData={preselectedSubData}
                      previewDetails={previewDetails}
                      hideHeading
                      isTaxCalculated={isTaxCalculated}
                    />
                  )}
                </div>
              </div>
              {/* PROCEED TO PAY BUTTON */}
              {activeRightTab === 'monthly' && (
                <p
                  style={{
                    fontSize: '14px',
                    color: '#ffffff',
                    fontWeight: 700,
                    margin: '0 0 4px 0',
                  }}
                >
                  {country === 'AU'
                    ? '*Excludes fees and incentives'
                    : '*Excludes taxes, fees, and incentives'}
                </p>
              )}
              <button
                type="button"
                onClick={() => {
                  setMobileStep(2)
                  setHasEnteredStep2(true)
                }}
                style={{
                  width: '100%',
                  height: '42px',
                  backgroundColor: '#FF5000',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '10px',
                  marginBottom: '16px',
                  padding: '10px 20px',
                  fontSize: '16px',
                  fontWeight: 700,
                  fontFamily: 'Open Sans',
                  lineHeight: '100%',
                  letterSpacing: '0%',
                  cursor: 'pointer',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                }}
              >
                <svg
                  width="18"
                  height="15"
                  viewBox="0 0 18 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.4019 14.4511C11.4039 14.4491 11.4059 14.4471 11.408 14.445L17.709 8.1522C17.7588 8.10221 17.8029 8.04696 17.8408 7.98746L17.9397 7.80626L17.9809 7.68271L17.9809 7.5921C18.0064 7.45326 18.0064 7.3109 17.9809 7.17202L17.9809 7.08967L17.9809 6.99083L17.9067 6.85079C17.8643 6.77276 17.8117 6.70071 17.7502 6.63662L11.408 0.319083C10.9883 -0.103963 10.3051 -0.106704 9.88203 0.312983C9.87998 0.314991 9.87797 0.317037 9.87593 0.319083C9.46906 0.745526 9.46906 1.41641 9.87593 1.84289L13.6319 5.60706C13.7911 5.76945 13.7885 6.03022 13.6261 6.18945C13.5506 6.26354 13.4494 6.3057 13.3436 6.30717L1.07911 6.30717C0.483169 6.30713 4.92943e-05 6.79017 9.21389e-06 7.38611C-2.89591e-05 7.98205 0.483015 8.46514 1.07895 8.46521L1.07911 8.46521L13.3436 8.46521C13.571 8.46842 13.7528 8.65537 13.7496 8.88278C13.7481 8.98857 13.706 9.08976 13.6319 9.16532L9.87593 12.913C9.46381 13.3406 9.46381 14.0175 9.87593 14.4451C10.2956 14.8681 10.9788 14.8709 11.4019 14.4511Z"
                    fill="white"
                  />
                </svg>
                PROCEED TO PAY
              </button>
            </div>
            {hasEnteredStep2 && (
              <div style={{display: mobileStep === 2 ? 'contents' : 'none'}}>
                {/* PAYMENT STEP */}
                {/* WHITE CARD */}
                <div
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    overflow: 'hidden',
                  }}
                >
                  {/* PAYMENT HEADER */}
                  {getPaymentMethods(country).length > 1 && (
                    <div
                      style={{
                        padding: '10px 16px 8px',
                        flexShrink: 0,
                      }}
                    >
                      <p className="select-payment-label">Select</p>
                      <p
                        className="payment-method-label"
                        style={{marginBottom: '10px', whiteSpace: 'nowrap'}}
                      >
                        Payment Method
                      </p>
                      {(() => {
                        const mobilePaymentMethods = getPaymentMethods(country)
                        const activeIndex = mobilePaymentMethods.findIndex(
                          m => m.value === paymentMethod?.value,
                        )
                        const tabs = mobilePaymentMethods.map(method => ({
                          label:
                            method.value === 'ach'
                              ? 'Transfer with ACH'
                              : method.value === 'becs'
                                ? 'BECS'
                                : 'Pay with Card',
                          value: method.value,
                        }))
                        return (
                          <div style={{width: '95%', margin: '0 auto'}}>
                            <Toggle
                              tabs={tabs}
                              activeIndex={activeIndex}
                              onChange={(_tab, index) =>
                                handleChangePaymentMethod(
                                  mobilePaymentMethods[index],
                                )
                              }
                              variant="pillFilled"
                              fullWidth
                            />
                          </div>
                        )
                      })()}
                    </div>
                  )}
                  {/* SCROLLABLE FORM */}
                  <div
                    className="payment-form-scroll"
                    style={{
                      flex: 1,
                      minHeight: 0,
                      overflowY: 'auto',
                      padding: '16px',
                      background: '#DDE1EA',
                      borderRadius: '10px 10px 0 0',
                      scrollbarGutter: 'stable',
                    }}
                  >
                    <PaymentMethodComponent
                      paymentState={dataJSON}
                      setRecurlyToken={setRecurlyToken}
                      setTaxCalculateStatus={setTaxCalculateStatus}
                      isTaxCalculated={isTaxCalculated}
                      calculateTaxes={calculateTaxes}
                      paymentUUID={paymentUUID}
                      accountInformation={
                        payloadData?.accountInformation ||
                        transitionData?.accountInformation ||
                        transitionData?.state?.accountInformation
                      }
                      handleChangePaymentMethod={handleChangePaymentMethod}
                      paymentMethod={paymentMethod}
                      setPaymentRequestState={() => {}}
                      country={country || 'US'}
                      keapPartnerLinkDetail={
                        transitionData?.state?.keapPartnerLinkDetail
                      }
                      hideToggle
                      allowCountryChange={allowCountryChange}
                      disclaimerText={getPaymentDisclaimer(
                        preselectedSubData?.plan_code?.plan,
                      )}
                    />
                  </div>
                  {/* STICKY FOOTER - T&C */}
                  <div
                    style={{
                      paddingInline: '16px',
                      paddingTop: '10px',
                      paddingBottom: '14px',
                      backgroundColor: '#DDE1EA',
                      flexShrink: 0,
                    }}
                  >
                    <AgreedTermNote
                      setCheckBoxesValues={setCheckBoxesValues}
                      checkBoxesValues={checkBoxesValues}
                      isAgreeCheckboxDisabled={isAgreeCheckboxDisabled}
                      isTaxCalculated={isTaxCalculated}
                      isKCCExist={isKCCExist}
                      showAdyenPrivacy={false}
                    />
                  </div>
                </div>
                {/* COMPLETE PURCHASE BUTTON */}
                <button
                  type="button"
                  onClick={handlePaymentDetailContinue}
                  disabled={!isTaxCalculated || !checkBoxesValues.agreedTerms}
                  style={{
                    width: '100%',
                    height: '42px',
                    backgroundColor:
                      !isTaxCalculated || !checkBoxesValues.agreedTerms
                        ? '#BFBFBF'
                        : '#FF5000',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '10px 20px',
                    fontSize: '16px',
                    fontWeight: 700,
                    fontFamily: 'Open Sans',
                    lineHeight: '100%',
                    letterSpacing: '0%',
                    cursor:
                      !isTaxCalculated || !checkBoxesValues.agreedTerms
                        ? 'not-allowed'
                        : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    flexShrink: 0,
                    marginBottom: '16px',
                  }}
                >
                  <svg
                    width="18"
                    height="15"
                    viewBox="0 0 18 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.4019 14.4511C11.4039 14.4491 11.4059 14.4471 11.408 14.445L17.709 8.1522C17.7588 8.10221 17.8029 8.04696 17.8408 7.98746L17.9397 7.80626L17.9809 7.68271L17.9809 7.5921C18.0064 7.45326 18.0064 7.3109 17.9809 7.17202L17.9809 7.08967L17.9809 6.99083L17.9067 6.85079C17.8643 6.77276 17.8117 6.70071 17.7502 6.63662L11.408 0.319083C10.9883 -0.103963 10.3051 -0.106704 9.88203 0.312983C9.87998 0.314991 9.87797 0.317037 9.87593 0.319083C9.46906 0.745526 9.46906 1.41641 9.87593 1.84289L13.6319 5.60706C13.7911 5.76945 13.7885 6.03022 13.6261 6.18945C13.5506 6.26354 13.4494 6.3057 13.3436 6.30717L1.07911 6.30717C0.483169 6.30713 4.92943e-05 6.79017 9.21389e-06 7.38611C-2.89591e-05 7.98205 0.483015 8.46514 1.07895 8.46521L1.07911 8.46521L13.3436 8.46521C13.571 8.46842 13.7528 8.65537 13.7496 8.88278C13.7481 8.98857 13.706 9.08976 13.6319 9.16532L9.87593 12.913C9.46381 13.3406 9.46381 14.0175 9.87593 14.4451C10.2956 14.8681 10.9788 14.8709 11.4019 14.4511Z"
                      fill="white"
                    />
                  </svg>
                  <span style={{fontWeight: 400}}>PAY NOW&nbsp;</span>
                  <span>
                    {(() => {
                      if (
                        displayPreview?.total != null &&
                        (isTaxCalculated || displayPreview.tax > 0)
                      ) {
                        return `$${Number(Math.max(0, displayPreview.total)).toFixed(2)}`
                      }
                      const t = computeTabTotal({
                        paymentDetailType: 'today',
                        cartDetailObj: transitionData.state.orderDetail,
                        previewDetails,
                        finalBillingAmountDetails,
                        couponList: transitionData.state.couponList?.length
                          ? transitionData.state.couponList
                          : (previewDetails?.coupons ?? []),
                        country,
                      })
                      return t != null ? `$${Number(t).toFixed(2)}` : ''
                    })()}
                    {country !== 'AU' &&
                      !(
                        displayPreview?.total != null &&
                        (isTaxCalculated || displayPreview.tax > 0)
                      ) &&
                      ' + Tax'}
                  </span>
                </button>
              </div>
            )}
          </div>
          {isAccountCreationFlow && !isLoadingTimelineCompleted && (
            <LoadingTimelineModal currentStep={currentStep.current} />
          )}
          {showPageSpinner && <AppSpinner />}
          {showErrorAccountModal.current && (
            <AccountFailedModalComponent
              linkId={
                transitionData?.state?.linkId ||
                navData?.state?.id ||
                navData?.state?.linkId
              }
              retryLimit={RETRY_COUNT_LIMIT}
              currentErrorStep={currentErrorStep.current}
              handleModalClose={() => {
                showErrorAccountModal.current = false
              }}
            />
          )}
        </div>
      ) : (
        <section
          className={!_isLoading ? '' : 'overlay--style'}
          style={{
            display: 'grid',
            gridTemplateColumns: '293fr 137fr',
            alignItems: 'stretch',
            width: '100%',
            gap: '26px',
            boxSizing: 'border-box',
            paddingInline: '24px',
          }}
        >
          {/* LEFT PANEL */}
          <section
            style={{
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              alignSelf: 'flex-start',
              maxHeight: isUsdOnlyCountry(country)
                ? 'calc(100vh - 164px)'
                : 'calc(100vh - 108px)',
            }}
          >
            <img
              src={thryvPeek}
              alt=""
              style={{
                position: 'absolute',
                top: '0',
                transform: 'translateY(-80%)',
                right: '20px',
                zIndex: 1,
                pointerEvents: 'none',
                width: '141px',
              }}
            />
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                overflow: 'hidden',
                maxHeight: 'calc(100vh - 108px)',
              }}
            >
              {/* PAYMENT HEADER */}
              <div
                style={{
                  paddingInline: '20px',
                  paddingBlock: '12px',
                  flexShrink: 0,
                }}
              >
                <section
                  className="d-flex"
                  style={{
                    alignItems: 'center',
                    gap: '24px',
                  }}
                >
                  <div>
                    <p className="select-payment-label">Select</p>
                    <p className="payment-method-label">Payment Method</p>
                  </div>
                  {(() => {
                    const desktopPaymentMethods = getPaymentMethods(country)
                    const activeIndex = desktopPaymentMethods.findIndex(
                      m => m.value === paymentMethod?.value,
                    )
                    const tabs = desktopPaymentMethods.map(method => ({
                      label:
                        method.value === 'ach'
                          ? 'Transfer with ACH'
                          : method.value === 'becs'
                            ? 'BECS'
                            : 'Pay with Card',
                      value: method.value,
                    }))
                    return (
                      <div
                        style={{
                          opacity: checkBoxesValues.agreedTerms ? 0.5 : 1,
                          cursor: checkBoxesValues.agreedTerms
                            ? 'not-allowed'
                            : 'auto',
                        }}
                      >
                        <Toggle
                          tabs={tabs}
                          activeIndex={activeIndex}
                          onChange={(_tab, index) =>
                            handleChangePaymentMethod(
                              desktopPaymentMethods[index],
                            )
                          }
                          variant="pillFilled"
                          renderTab={(tab, isActive) => (
                            <>
                              {tab.value === 'ach' ? (
                                <AchTransferIcon
                                  color={isActive ? '#ffffff' : '#3D5199'}
                                />
                              ) : (
                                <CardPaymentIcon
                                  color={isActive ? '#ffffff' : '#3D5199'}
                                />
                              )}
                              {tab.label}
                              {isActive && <ArrowRightIcon />}
                            </>
                          )}
                        />
                      </div>
                    )
                  })()}
                </section>
              </div>
              {/* SCROLLABLE FORM */}
              <div
                className="payment-form-scroll"
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  padding: '20px',
                  scrollbarGutter: 'stable',
                  background: '#DDE1EA',
                  borderRadius: '10px 10px 0px 0px',
                }}
              >
                <PaymentMethodComponent
                  paymentState={dataJSON}
                  setRecurlyToken={setRecurlyToken}
                  setTaxCalculateStatus={setTaxCalculateStatus}
                  isTaxCalculated={isTaxCalculated}
                  calculateTaxes={calculateTaxes}
                  paymentUUID={paymentUUID}
                  accountInformation={
                    payloadData?.accountInformation ||
                    transitionData?.accountInformation ||
                    transitionData?.state?.accountInformation
                  }
                  handleChangePaymentMethod={handleChangePaymentMethod}
                  paymentMethod={paymentMethod}
                  setPaymentRequestState={() => {}}
                  country={country || 'US'}
                  keapPartnerLinkDetail={
                    transitionData?.state?.keapPartnerLinkDetail
                  }
                  hideToggle
                  disclaimerText={getPaymentDisclaimer(
                    preselectedSubData?.plan_code?.plan,
                  )}
                />
              </div>
              {/* STICKY FOOTER */}
              <div
                style={{
                  paddingLeft: '20px',
                  paddingRight: '32px',
                  paddingTop: '10px',
                  paddingBottom: '14px',
                  backgroundColor: '#DDE1EA',
                  flexShrink: 0,
                }}
              >
                <AgreedTermNote
                  setCheckBoxesValues={setCheckBoxesValues}
                  checkBoxesValues={checkBoxesValues}
                  isAgreeCheckboxDisabled={isAgreeCheckboxDisabled}
                  isTaxCalculated={isTaxCalculated}
                  isKCCExist={isKCCExist}
                  showAdyenPrivacy={false}
                />
              </div>
            </div>
          </section>

          {/* RIGHT PANEL */}
          <section
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              height: isUsdOnlyCountry(country)
                ? 'calc(100vh - 164px)'
                : 'calc(100vh - 108px)',
              maxHeight: isUsdOnlyCountry(country)
                ? 'calc(100vh - 164px)'
                : 'calc(100vh - 108px)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* TABS HEADER */}
            <div style={{flexShrink: 0, paddingInline: '10px'}}>
              <GradientTabs
                activeTab={activeRightTab}
                onChange={setActiveRightTab}
                variant="payment"
              >
                <GradientTab id="today">
                  <div className="label">
                    <p
                      className={`tab-label-title${activeRightTab === 'today' ? ' active' : ''}`}
                    >
                      Due Today{' '}
                      <span>
                        (
                        {(() => {
                          const alreadyAddedCodes = new Set(
                            (cartDetailObj?.plans || [])
                              .filter(p => p.isAlreadyAdded)
                              .map(p => p.code)
                              .filter(Boolean),
                          )
                          // Core links count from the cart (same rule as the
                          // rows); the preview only lists today's charges.
                          const coreCount = getCoreTabItemCount(
                            cartDetailObj,
                            'today',
                          )
                          if (coreCount !== null) return coreCount
                          return previewDetails?.paymentPreview
                            ? [
                                ...(previewDetails.paymentPreview.apps || []),
                                ...(previewDetails.paymentPreview.centers ||
                                  []),
                                ...(previewDetails.paymentPreview.oneTimes ||
                                  []),
                              ].filter(
                                item =>
                                  !alreadyAddedCodes.has(
                                    item.planCode || item.code,
                                  ),
                              ).length
                            : (getCoreTabItemCount(cartDetailObj, 'today') ??
                                (cartDetailObj?.plans || []).filter(
                                  p =>
                                    !NON_ADDED_PLANS.includes(p.code) &&
                                    !p.isAlreadyAdded,
                                ).length)
                        })()}{' '}
                        Items)
                      </span>
                    </p>
                  </div>
                  <p
                    className={`tab-price${activeRightTab === 'today' ? ' active' : ''}`}
                  >
                    {(() => {
                      if (isSummaryBusy) return ''
                      if (
                        displayPreview?.total != null &&
                        (isTaxCalculated || displayPreview.tax > 0)
                      ) {
                        return formatPrice({
                          amount: Math.max(0, displayPreview.total),
                          country,
                        })
                      }
                      const t = computeTabTotal({
                        paymentDetailType: 'today',
                        cartDetailObj: transitionData.state.orderDetail,
                        previewDetails,
                        finalBillingAmountDetails,
                        couponList: transitionData.state.couponList?.length
                          ? transitionData.state.couponList
                          : (previewDetails?.coupons ?? []),
                        country,
                      })
                      if (t == null) return ''
                      const showPlusTax =
                        country !== 'AU' &&
                        !(isTaxCalculated || displayPreview?.tax > 0)
                      return showPlusTax
                        ? `${formatPrice({amount: t, country})} + Tax`
                        : formatPrice({amount: t, country})
                    })()}
                  </p>
                </GradientTab>
                <GradientTab id="monthly">
                  <div className="label blue">
                    <p
                      className={`tab-label-title${activeRightTab === 'monthly' ? ' active' : ''}`}
                    >
                      {(() => {
                        const annualT = computeTabTotal({
                          paymentDetailType: 'annual',
                          cartDetailObj: transitionData.state.orderDetail,
                          country,
                        })
                        const monthlyT = computeTabTotal({
                          paymentDetailType: 'monthly',
                          cartDetailObj: transitionData.state.orderDetail,
                          country,
                        })
                        return annualT > 0 && monthlyT === 0
                          ? 'Due Annually'
                          : 'Due Monthly'
                      })()}{' '}
                      <span>
                        (
                        {(() => {
                          const annualT = computeTabTotal({
                            paymentDetailType: 'annual',
                            cartDetailObj: transitionData.state.orderDetail,
                            country,
                          })
                          const monthlyT = computeTabTotal({
                            paymentDetailType: 'monthly',
                            cartDetailObj: transitionData.state.orderDetail,
                            country,
                          })
                          const isAnnualView = annualT > 0 && monthlyT === 0
                          const isAnnualItem = p =>
                            String(p.code || '')
                              .toLowerCase()
                              .includes('annual') ||
                            String(p.name || '')
                              .toLowerCase()
                              .includes('annual') ||
                            String(p.name || '')
                              .toLowerCase()
                              .includes('yearly')
                          const coreMonthlyCount = getCoreTabItemCount(
                            cartDetailObj,
                            isAnnualView ? 'annual' : 'monthly',
                          )
                          if (coreMonthlyCount !== null) {
                            return coreMonthlyCount
                          }
                          if (previewDetails?.paymentPreview) {
                            return [
                              ...(previewDetails.paymentPreview.apps || []),
                              ...(previewDetails.paymentPreview.centers || []),
                            ].length
                          }
                          const coreCount = getCoreTabItemCount(
                            cartDetailObj,
                            isAnnualView ? 'annual' : 'monthly',
                          )
                          if (coreCount !== null) return coreCount
                          return (cartDetailObj?.plans || []).filter(
                            p =>
                              !NON_ADDED_PLANS.includes(p.code) &&
                              p.prodType !== 'OneTimeFees' &&
                              p.prodType !== 'oneTimeFee' &&
                              p.section !== 'oneTimeFees' &&
                              p.groupKey !== 'oneTimeFees' &&
                              (isAnnualView
                                ? isAnnualItem(p)
                                : !isAnnualItem(p)),
                          ).length
                        })()}{' '}
                        Items)
                      </span>
                    </p>
                  </div>
                  <p
                    className={`tab-price${activeRightTab === 'monthly' ? ' active' : ''}`}
                  >
                    {(() => {
                      if (isSummaryBusy) return ''
                      const annualT = computeTabTotal({
                        paymentDetailType: 'annual',
                        cartDetailObj: transitionData.state.orderDetail,
                        country,
                      })
                      const monthlyT = computeTabTotal({
                        paymentDetailType: 'monthly',
                        cartDetailObj: transitionData.state.orderDetail,
                        country,
                      })
                      const t =
                        annualT > 0 && monthlyT === 0 ? annualT : monthlyT
                      if (t == null) return ''
                      if (country === 'AU') {
                        return (
                          <>
                            {formatPrice({amount: t, country})}
                            <span style={{color: '#FF5000'}}>*</span>
                          </>
                        )
                      }
                      return (
                        <>
                          {`${formatPrice({amount: t, country})} + Tax`}
                          {country !== 'NZ' && (
                            <span style={{color: '#FF5000'}}>*</span>
                          )}
                        </>
                      )
                    })()}
                  </p>
                </GradientTab>
              </GradientTabs>
            </div>

            {/* SCROLLABLE PRODUCTS */}
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <div
                className="payment-form-scroll"
                style={{
                  position: 'absolute',
                  inset: 0,
                  overflowY: 'auto',
                  padding: '10px',
                  scrollbarGutter: 'stable',
                  background: '#DDE1EA',
                  borderRadius: '10px',
                  boxShadow: '0px 0px 22px 0px #00000040 inset',
                }}
              >
                {isSummaryBusy ? (
                  <SummarySpinner />
                ) : activeRightTab === 'today' ? (
                  <KCPPaymentDetails
                    country={country}
                    locationNames={locationNames}
                    paymentState={dataJSON}
                    paymentDetailType="today"
                    cartDetailObj={transitionData.state.orderDetail}
                    finalBillingAmountDetails={finalBillingAmountDetails}
                    currentScreen={'paymentInfo'}
                    couponList={
                      transitionData.state.couponList?.length
                        ? transitionData.state.couponList
                        : (previewDetails?.coupons ?? [])
                    }
                    previewDetails={previewDetails}
                    preselectedSubData={preselectedSubData}
                    hideHeading
                    isTaxCalculated={isTaxCalculated}
                  />
                ) : (
                  <KCPPaymentDetails
                    country={country}
                    locationNames={locationNames}
                    paymentState={dataJSON}
                    cartDetailObj={transitionData.state.orderDetail}
                    paymentDetailType="monthly"
                    currentScreen={'paymentInfo'}
                    preselectedSubData={preselectedSubData}
                    previewDetails={previewDetails}
                    hideHeading
                    isTaxCalculated={isTaxCalculated}
                  />
                )}
              </div>
            </div>

            {/* BUTTON FOOTER */}
            <div
              style={{
                padding: '10px',
                borderTop: '1px solid #ECEEF1',
                backgroundColor: 'white',
                flexShrink: 0,
              }}
            >
              {activeRightTab === 'monthly' && (
                <p
                  style={{
                    fontSize: '14px',
                    color: '#FF5000',
                    fontWeight: 700,
                    margin: '0 4px 4px 4px',
                  }}
                >
                  {country === 'AU'
                    ? '*Excludes fees and incentives'
                    : '*Excludes taxes, fees, and incentives'}
                </p>
              )}
              <button
                type="button"
                className="complete-purchase-btn ad-continue"
                onClick={handlePaymentDetailContinue}
                disabled={
                  !isTaxCalculated ||
                  !checkBoxesValues.agreedTerms ||
                  _isLoading
                }
              >
                {_isLoading ? (
                  <span
                    style={{
                      display: 'inline-block',
                      width: '18px',
                      height: '18px',
                      border: '3px solid rgba(255,255,255,0.4)',
                      borderTopColor: '#fff',
                      borderRadius: '50%',
                      animation: 'spin 0.7s linear infinite',
                    }}
                  />
                ) : (
                  <>
                    <svg
                      width="18"
                      height="15"
                      viewBox="0 0 18 15"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{display: 'block', flexShrink: 0}}
                    >
                      <path
                        d="M11.4019 14.4511C11.4039 14.4491 11.4059 14.4471 11.408 14.445L17.709 8.15221C17.7588 8.10221 17.8029 8.04696 17.8408 7.98746L17.9397 7.80626L17.9809 7.68271L17.9809 7.5921C18.0064 7.45326 18.0064 7.3109 17.9809 7.17202L17.9809 7.08967L17.9809 6.99083L17.9067 6.85079C17.8643 6.77276 17.8117 6.70072 17.7502 6.63662L11.408 0.319084C10.9883 -0.103962 10.3051 -0.106703 9.88203 0.312984C9.87998 0.314991 9.87797 0.317038 9.87593 0.319084C9.46906 0.745527 9.46906 1.41641 9.87593 1.84289L13.6319 5.60706C13.7911 5.76945 13.7885 6.03022 13.6261 6.18945C13.5506 6.26354 13.4494 6.3057 13.3436 6.30717L1.07911 6.30717C0.483169 6.30713 4.92943e-05 6.79017 9.21389e-06 7.38611C-2.89591e-05 7.98205 0.483015 8.46514 1.07895 8.46521L1.07911 8.46521L13.3436 8.46521C13.571 8.46842 13.7528 8.65537 13.7496 8.88278C13.7481 8.98857 13.706 9.08976 13.6319 9.16532L9.87593 12.913C9.46381 13.3406 9.46381 14.0175 9.87593 14.4451C10.2956 14.8681 10.9788 14.8709 11.4019 14.4511Z"
                        fill="white"
                      />
                    </svg>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                      }}
                    >
                      <span
                        style={{
                          fontFamily: 'inherit',
                          fontWeight: 400,
                          fontSize: '16px',
                          lineHeight: '100%',
                        }}
                      >
                        PAY NOW&nbsp;
                      </span>
                      <span
                        style={{
                          fontFamily: 'inherit',
                          fontWeight: 700,
                          fontSize: '16px',
                          lineHeight: '100%',
                        }}
                      >
                        {(() => {
                          if (
                            displayPreview?.total != null &&
                            (isTaxCalculated || displayPreview.tax > 0)
                          ) {
                            return `$${Number(
                              Math.max(0, displayPreview.total),
                            ).toFixed(2)}`
                          }
                          const t = computeTabTotal({
                            paymentDetailType: 'today',
                            cartDetailObj: transitionData.state.orderDetail,
                            previewDetails,
                            finalBillingAmountDetails,
                            couponList: transitionData.state.couponList?.length
                              ? transitionData.state.couponList
                              : (previewDetails?.coupons ?? []),
                            country,
                          })
                          return t != null
                            ? `$${Number(t).toFixed(2)}${country === 'AU' ? '' : ' + Tax'}`
                            : ''
                        })()}
                      </span>
                    </span>
                  </>
                )}
              </button>
            </div>
          </section>
          {isAccountCreationFlow && !isLoadingTimelineCompleted && (
            <LoadingTimelineModal currentStep={currentStep.current} />
          )}
          {showPageSpinner && <AppSpinner />}
          {showErrorAccountModal.current && (
            <AccountFailedModalComponent
              linkId={
                transitionData?.state?.linkId ||
                navData?.state?.id ||
                navData?.state?.linkId
              }
              retryLimit={RETRY_COUNT_LIMIT}
              currentErrorStep={currentErrorStep.current}
              handleModalClose={() => {
                showErrorAccountModal.current = false
              }}
            />
          )}
        </section>
      )}
    </>
  )
}
